import { useState, useRef, useEffect, useCallback, useMemo, Component } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import './Editor.css'
import YouTube from 'react-youtube'
import ChordSelectorModal from '../ChordSelectorModal'
import VideoCockpit from '../VideoCockpit'
import SaveExerciseModal from '../components/SaveExerciseModal'
import ConfirmExitModal from '../components/ConfirmExitModal'
import VideoImport from '../components/VideoImport'
import PWAInstallPrompt from '../components/PWAInstallPrompt'
import { useAuth } from '../contexts/AuthContext'
import { sanitizeReturnTo } from '../utils/navigationReturnTo'
import { getExerciseById, createExercise, updateExercise, getExerciseDominantMode } from '../services/exerciseService'
import { resizeImageToDataUrl, getImageFromClipboard, getImageFromPasteEvent } from '../services/thumbnailService'
import { preloadSmartFillForVideo, isOpusApiConfigured } from '../services/opusApi'
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Plus,
  X,
  Save,
  LogIn,
  ArrowLeft,
  Eye,
  Flag,
  RotateCcw,
  ImageIcon,
  Upload,
  Loader2,
  Crop
} from 'lucide-react'
import { DEGREE_TO_FUNCTIONS, getDefaultFunctionForChord, buildChordContext } from '../utils/riemannFunctions'
import { getDegreeLabelForMode } from '../utils/modalScales'
import ChordLabel from '../components/ChordLabel'
import { inferBorrowedFromDegree } from '../utils/chordInference'
import { computePedalGroups, getImplicitPedalDegree } from '../utils/pedalContinuity'
import { asDisplayText } from '../utils/safeText'
import { useBackdropDismiss } from '../hooks/useBackdropDismiss'

// Error boundary pour isoler les erreurs du lecteur YouTube (ex. www-widgetapi.js)
class YouTubePlayerErrorBoundary extends Component {
  state = { hasError: false }
  static getDerivedStateFromError() { return { hasError: true } }
  componentDidCatch(err, info) { console.warn('YouTube player error:', err, info) }
  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-zinc-900 text-white/80 text-sm p-4 text-center">
          Lecteur temporairement indisponible. Cliquez sur «&nbsp;Changer&nbsp;» pour réessayer ou choisir une autre vidéo.
        </div>
      )
    }
    return this.props.children
  }
}

function Editor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const safeReturnTo = sanitizeReturnTo(location.state?.returnTo, '/dashboard')
  const { user, signInWithGoogle, isAdminTeacher } = useAuth()
  const [videoId, setVideoId] = useState('')
  const [startTime, setStartTime] = useState(0)
  const [endTime, setEndTime] = useState(60)
  const [isPlaying, setIsPlaying] = useState(false)
  const [markers, setMarkers] = useState([])
  const [currentTime, setCurrentTime] = useState(0)
  const [isRecording, setIsRecording] = useState(false)
  const [flash, setFlash] = useState(false)
  const [chordData, setChordData] = useState({})
  const [chordEndTimes, setChordEndTimes] = useState({}) // { [markerIndex]: number } fin optionnelle par accord
  const [selectedMarkerId, setSelectedMarkerId] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [videoDuration, setVideoDuration] = useState(0)
  const [videoTitle, setVideoTitle] = useState('')
  const [isEditingUrl, setIsEditingUrl] = useState(true)
  const [showVideoSearch, setShowVideoSearch] = useState(false)
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState(null)
  const [loading, setLoading] = useState(!!id)
  const [exerciseId, setExerciseId] = useState(id || null)
  const [loadedAuthorId, setLoadedAuthorId] = useState(null)
  const [isConfirmExitModalOpen, setIsConfirmExitModalOpen] = useState(false)
  /** 'dashboard' = retour liste ; 'preview' = ouvrir l'aperçu. Définit le texte de la modale et l'action au confirm. */
  const [pendingExitAction, setPendingExitAction] = useState(null) // null | 'dashboard' | 'preview'
  const [lastSavedState, setLastSavedState] = useState(null) // État sauvegardé pour comparaison
  const [isDraggingTime, setIsDraggingTime] = useState(null) // 'in' | 'out' | 'marker-{index}' | 'crop-{index}' | null
  const [editingTimer, setEditingTimer] = useState(null) // { type: 'in' } | { type: 'out' } | { type: 'marker', index } | { type: 'crop', index } | null
  const [timerEditInputValue, setTimerEditInputValue] = useState('')
  const [timerEditError, setTimerEditError] = useState(null)
  const [dragStartX, setDragStartX] = useState(0)
  const [dragStartTime, setDragStartTime] = useState(0)
  const [draggingMarkerIndex, setDraggingMarkerIndex] = useState(null)
  const [longPressPendingIndex, setLongPressPendingIndex] = useState(null)
  const [longPressArmedIndex, setLongPressArmedIndex] = useState(null)
  const [loadedAutoTags, setLoadedAutoTags] = useState([])
  const [loadedTonality, setLoadedTonality] = useState('C')
  const [loadedSection, setLoadedSection] = useState(null)
  const [loadedMusicCategory, setLoadedMusicCategory] = useState(null)
  const [loadedFormation, setLoadedFormation] = useState(null) // array of formation ids
  const [loadedGenre, setLoadedGenre] = useState(null)
  const [loadedYear, setLoadedYear] = useState(null)
  const [loadedMusicalEra, setLoadedMusicalEra] = useState(null)
  const [loadedComposer, setLoadedComposer] = useState('')
  const [loadedComposerGivenName, setLoadedComposerGivenName] = useState('')
  const [loadedComposerFamilyName, setLoadedComposerFamilyName] = useState('')
  const [loadedWorkTitle, setLoadedWorkTitle] = useState('')
  const [loadedMovementTitle, setLoadedMovementTitle] = useState('')
  const [loadedExerciseTitle, setLoadedExerciseTitle] = useState('')
  const [loadedSubtitle, setLoadedSubtitle] = useState('')
  const [loadedDifficulty, setLoadedDifficulty] = useState('')
  const [loadedPublicPreview, setLoadedPublicPreview] = useState(false)
  const [youtubePlayerError, setYoutubePlayerError] = useState(false)
  const [thumbnailUrl, setThumbnailUrl] = useState(null)
  const [isThumbnailLoading, setIsThumbnailLoading] = useState(false)
  const [thumbnailMessage, setThumbnailMessage] = useState(null)
  const [showThumbnailPanel, setShowThumbnailPanel] = useState(false)
  const thumbnailInputRef = useRef(null)
  const thumbnailPanelRef = useRef(null)

  const playerRef = useRef(null)
  const intervalRef = useRef(null)
  const currentTimeRef = useRef(0)
  const timelineRef = useRef(null)
  const timelineContentRef = useRef(null)
  const chordRibbonRef = useRef(null)
  const inTimerRef = useRef(null)
  const outTimerRef = useRef(null)
  const fadeIntervalRef = useRef(null)
  const hasDraggedSinceTimerDownRef = useRef(false)
  const dragLastXRef = useRef(0)
  const dragLastTimeRef = useRef(0)
  const dragTotalPxRef = useRef(0)
  const dragLastEventTimeRef = useRef(0)
  const playheadRef = useRef(null)
  const lastSetStateTimeRef = useRef(0)
  const lastSegmentIndexRef = useRef(null)
  const DRAG_SENSITIVITY = 0.35
  const DRAG_DEADZONE_PX = 20
  const LONG_PRESS_MS = 450
  const LONG_PRESS_CANCEL_PX = 8
  const longPressRef = useRef(null)
  const timerAnchorRef = useRef(null)
  const timerEditInputRef = useRef(null)
  const timerJustOpenedRef = useRef(false)
  const videoZoneRef = useRef(null)
  const [editorTimelineViewportWidth, setEditorTimelineViewportWidth] = useState(0)

  // Plage IN-OUT pour la timeline (déclaré tôt pour être utilisé dans les useEffects)
  const selectionDuration = endTime - startTime
  const timelineRange = Math.max(selectionDuration, 0.0001)

  // Charger l'exercice existant si on est en mode édition
  useEffect(() => {
    if (id && user) {
      loadExercise(id)
    } else if (id && !user) {
      navigate('/')
    }
  }, [id, user])

  const canEditLoadedExercise = !loadedAuthorId || loadedAuthorId === user?.uid || isAdminTeacher

  useEffect(() => {
    if (!loading && id && loadedAuthorId && !canEditLoadedExercise) {
      setSaveMessage({ type: 'error', text: 'Vous n\'avez pas les droits pour éditer cet exercice' })
      setTimeout(() => navigate('/dashboard'), 2000)
    }
  }, [loading, id, loadedAuthorId, canEditLoadedExercise, navigate])

  // Intercepter les erreurs asynchrones du widget YouTube (postMessage/callbacks) que l'Error Boundary ne capture pas
  useEffect(() => {
    const handleError = (e) => {
      const isYoutubeWidget = e.filename?.includes('widgetapi') || e.message?.includes("reading 'src'")
      if (isYoutubeWidget) {
        e.preventDefault?.()
        setYoutubePlayerError(true)
        return true
      }
    }
    window.addEventListener('error', handleError)
    return () => window.removeEventListener('error', handleError)
  }, [])

  // Réinitialiser l'état d'erreur quand on change de vidéo
  const currentVideoIdRef = useRef(null)
  useEffect(() => {
    if (videoId !== currentVideoIdRef.current) {
      currentVideoIdRef.current = videoId
      setYoutubePlayerError(false)
    }
  }, [videoId])

  // Hauteur visible sur mobile (Visual Viewport) : évite que timeline + contrôles soient coupés sous la barre d’URL
  useEffect(() => {
    const setVvh = () => {
      const h = window.visualViewport?.height ?? window.innerHeight
      document.documentElement.style.setProperty('--vvh', `${Math.round(h)}px`)
    }
    setVvh()
    const vv = window.visualViewport
    if (vv) {
      vv.addEventListener('resize', setVvh)
      vv.addEventListener('scroll', setVvh)
      return () => {
        vv.removeEventListener('resize', setVvh)
        vv.removeEventListener('scroll', setVvh)
        document.documentElement.style.removeProperty('--vvh')
      }
    }
    return () => document.documentElement.style.removeProperty('--vvh')
  }, [])

  // Verrouiller le scroll du body en mode éditeur (comme Player)
  useEffect(() => {
    document.body.classList.add('editor-active')
    return () => document.body.classList.remove('editor-active')
  }, [])

  // Détecter les partages depuis Web Share Target (Android PWA)
  useEffect(() => {
    // Parser les query params depuis l'URL (HashRouter ne gère pas query params dans hash)
    const urlParams = new URLSearchParams(window.location.search)
    const sharedUrl = urlParams.get('url') || urlParams.get('text')
    
    if (sharedUrl) {
      // Extraire l'ID YouTube de l'URL partagée
      const videoIdFromShare = extractVideoId(sharedUrl)
      
      // Vérifier si c'est une URL YouTube valide (extractVideoId retourne l'ID ou l'URL originale)
      const isValidYouTubeUrl = /(?:youtube\.com|youtu\.be)/.test(sharedUrl)
      
      if (isValidYouTubeUrl && videoIdFromShare && videoIdFromShare.length === 11) {
        // Charger la vidéo automatiquement
        setVideoId(videoIdFromShare)
        setIsEditingUrl(false)
        setShowVideoSearch(false)
        
        // Nettoyer les paramètres d'URL après traitement
        window.history.replaceState({}, '', window.location.pathname + window.location.hash)
        
        // Afficher un message de confirmation
        setSaveMessage({ 
          type: 'success', 
          text: 'Vidéo reçue depuis le partage !' 
        })
        setTimeout(() => setSaveMessage(null), 3000)
      } else if (sharedUrl && !isValidYouTubeUrl) {
        // Si ce n'est pas une URL YouTube valide
        setSaveMessage({ 
          type: 'error', 
          text: 'L\'URL partagée ne semble pas être une vidéo YouTube valide' 
        })
        setTimeout(() => setSaveMessage(null), 3000)
        window.history.replaceState({}, '', window.location.pathname + window.location.hash)
      }
    }
  }, [])

  const loadExercise = async (exerciseId) => {
    try {
      setLoading(true)
      const exercise = await getExerciseById(exerciseId)
      
      if (!exercise) {
        setSaveMessage({ type: 'error', text: 'Exercice introuvable' })
        setTimeout(() => navigate('/'), 2000)
        return
      }

      setLoadedAuthorId(exercise.authorId || null)

      if (exercise.video) {
        setVideoId(exercise.video.id)
        setVideoTitle(exercise.video.title || '')
        setVideoDuration(exercise.video.duration || 0)
      }

      if (exercise.settings) {
        setStartTime(exercise.settings.startTime || 0)
        setEndTime(exercise.settings.endTime || 60)
      }

      if (exercise.markers && exercise.markers.length > 0) {
        const loadedMarkers = exercise.markers.map(m => m.time)
        const loadedChordData = {}
        const loadedChordEndTimes = {}
        exercise.markers.forEach((marker, index) => {
          if (marker.chord) {
            loadedChordData[index] = marker.chord
          }
          if (marker.endTime != null && !Number.isNaN(Number(marker.endTime))) {
            loadedChordEndTimes[index] = Number(marker.endTime)
          }
        })
        setMarkers(loadedMarkers)
        setChordData(loadedChordData)
        setChordEndTimes(loadedChordEndTimes)
      } else {
        setChordEndTimes({})
      }

      setExerciseId(exerciseId)
      setThumbnailUrl(exercise.thumbnailUrl || null)
      setLoadedAutoTags(exercise.autoTags && Array.isArray(exercise.autoTags) ? [...exercise.autoTags] : [])
      setLoadedTonality(exercise.metadata?.tonality ?? 'C')
      setLoadedSection(exercise.metadata?.section ?? null)
      setLoadedMusicCategory(exercise.metadata?.musicCategory ?? null)
      const formation = exercise.metadata?.formation
      setLoadedFormation(Array.isArray(formation) ? formation : (formation ? [formation] : null))
      setLoadedGenre(exercise.metadata?.genre ?? null)
      setLoadedYear(exercise.metadata?.year ?? null)
      setLoadedMusicalEra(exercise.metadata?.musicalEra ?? null)
      setLoadedComposer(exercise.metadata?.composer ?? '')
      setLoadedComposerGivenName(exercise.metadata?.composerGivenName ?? '')
      setLoadedComposerFamilyName(exercise.metadata?.composerFamilyName ?? '')
      setLoadedWorkTitle(exercise.metadata?.workTitle ?? '')
      setLoadedMovementTitle(exercise.metadata?.movementTitle ?? '')
      setLoadedExerciseTitle(exercise.metadata?.exerciseTitle ?? '')
      setLoadedSubtitle(exercise.metadata?.subtitle ?? '')
      setLoadedDifficulty(exercise.metadata?.difficulty ?? '')
      setLoadedPublicPreview(!!exercise.publicPreview)
      setIsEditingUrl(false)
      setShowVideoSearch(false)
      
      // Sauvegarder l'état initial comme état sauvegardé
      const loadedMarkers = exercise.markers ? exercise.markers.map(m => m.time) : []
      const loadedChordData = {}
      const loadedChordEndTimes = {}
      if (exercise.markers) {
        exercise.markers.forEach((marker, index) => {
          if (marker.chord) {
            loadedChordData[index] = marker.chord
          }
          if (marker.endTime != null && !Number.isNaN(Number(marker.endTime))) {
            loadedChordEndTimes[index] = Number(marker.endTime)
          }
        })
      }
      setLastSavedState({
        markers: loadedMarkers,
        chordData: loadedChordData,
        chordEndTimes: loadedChordEndTimes,
        startTime: exercise.settings?.startTime || 0,
        endTime: exercise.settings?.endTime || 60,
        videoId: exercise.video?.id || ''
      })
    } catch (error) {
      console.error('Erreur lors du chargement de l\'exercice:', error)
      setSaveMessage({ type: 'error', text: 'Erreur lors du chargement de l\'exercice' })
      setTimeout(() => navigate('/'), 2000)
    } finally {
      setLoading(false)
    }
  }

  // Extraire l'ID de la vidéo YouTube depuis l'URL
  const extractVideoId = (url) => {
    if (!url) return ''
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)
    return match ? match[1] : url
  }

  // Gestion du lecteur YouTube
  const handleReady = (event) => {
    playerRef.current = event.target
    
    const initializeSelection = () => {
      try {
        const duration = event.target.getDuration()
        if (duration && duration > 0 && !isNaN(duration)) {
          setVideoDuration(duration)
          if (endTime === 60 && startTime === 0) {
            setStartTime(0)
            setEndTime(duration)
          }
        }
        
        try {
          const videoData = event.target.getVideoData()
          if (videoData && videoData.title) {
            setVideoTitle(videoData.title)
          }
        } catch (titleError) {
          console.error('Erreur lors de la récupération du titre:', titleError)
        }
      } catch (error) {
        // La vidéo n'est peut-être pas encore chargée
      }
    }
    
    setTimeout(initializeSelection, 500)
    setTimeout(initializeSelection, 1500)
    setTimeout(initializeSelection, 3000)
  }

  // Gestion des changements d'état du lecteur YouTube
  const handleStateChange = (event) => {
    if (!playerRef.current) return
    
    const state = event.data
    // YouTube.PlayerState.PLAYING = 1
    // YouTube.PlayerState.PAUSED = 2
    // YouTube.PlayerState.ENDED = 0
    
    if (state === 1) { // PLAYING
      // Si la lecture démarre depuis le bouton YouTube natif
      if (!isPlaying) {
        setIsPlaying(true)
        
        // Vérifier si on est avant le début de l'extrait ou après la fin
        try {
          const currentTime = playerRef.current.getCurrentTime()
          
          // Si on est avant le début ou après la fin, aller au début de l'extrait
          if (currentTime < startTime || currentTime >= endTime) {
            playerRef.current.seekTo(startTime, true)
            setCurrentTime(startTime)
            currentTimeRef.current = startTime
          }
        } catch (error) {
          console.error('Erreur lors de la vérification du temps:', error)
        }
      }
    } else if (state === 2 || state === 0) { // PAUSED ou ENDED
      // Si la lecture s'arrête, synchroniser l'état React
      if (isPlaying) {
        setIsPlaying(false)
      }
      
      // Si la vidéo est terminée, s'assurer qu'on est à la fin de l'extrait
      if (state === 0) {
        if (playerRef.current) {
          try {
            const currentTime = playerRef.current.getCurrentTime()
            // Si on est après la fin de l'extrait, revenir à la fin
            if (currentTime > endTime) {
              playerRef.current.seekTo(endTime, true)
              setCurrentTime(endTime)
              currentTimeRef.current = endTime
            }
          } catch (error) {
            console.error('Erreur lors de la vérification de fin:', error)
          }
        }
      }
    }
  }

  useEffect(() => {
    if (!videoId || !videoId.trim()) return
    if (!playerRef?.current) return

    const updateDuration = () => {
      if (!playerRef?.current) return
      try {
        const duration = playerRef.current.getDuration()
        if (duration && duration > 0 && !isNaN(duration)) {
          setVideoDuration(duration)
          if (endTime === 60 && startTime === 0) {
            setEndTime(duration)
          }
        }
        
        if (!videoTitle || videoTitle === 'Vidéo YouTube') {
          try {
            const videoData = playerRef.current.getVideoData()
            if (videoData && videoData.title) {
              setVideoTitle(videoData.title)
            }
          } catch (titleError) {
            console.error('Erreur lors de la récupération du titre:', titleError)
          }
        }
      } catch (error) {
        // La vidéo n'est peut-être pas encore chargée
      }
    }

    const timeout1 = setTimeout(updateDuration, 500)
    const timeout2 = setTimeout(updateDuration, 1500)
    const timeout3 = setTimeout(updateDuration, 3000)
    const interval = setInterval(updateDuration, 2000)

    return () => {
      clearTimeout(timeout1)
      clearTimeout(timeout2)
      clearTimeout(timeout3)
      clearInterval(interval)
    }
  }, [videoId, playerRef, endTime, startTime, videoTitle])

  // Préchargement smart-fill (parse-title + movements) pour la modale de sauvegarde
  useEffect(() => {
    const id = extractVideoId(videoId) || videoId
    if (!id || id.length !== 11 || !isOpusApiConfigured()) return
    preloadSmartFillForVideo({
      videoId: id,
      videoTitle: videoTitle || '',
      getVideoTitle: () => {
        try {
          return playerRef.current?.getVideoData?.()?.title ?? null
        } catch {
          return null
        }
      },
    })
  }, [videoId, videoTitle])

  useEffect(() => {
    if (!isPlaying && window.editorFadeIntervals) {
      window.editorFadeIntervals.forEach(({ cleanup }) => {
        if (cleanup) cleanup()
      })
      window.editorFadeIntervals = []
    }
  }, [isPlaying])

  // Suivi du temps pendant la lecture avec requestAnimationFrame pour une animation fluide
  useEffect(() => {
    let animationFrameId = null
    let isRunning = true
    const fadeDuration = 0.5 // Durée du fade out en secondes
    
    const updateTime = () => {
      if (!isRunning || !isPlaying || !playerRef.current) {
        return
      }
      
        try {
          const time = playerRef.current.getCurrentTime()
          // Si le temps lu est très différent de ce qu'on attend (après un seek), ignorer cette frame
          // pour éviter de détecter immédiatement la fin après un seek
          const expectedTime = currentTimeRef.current || time
          if (Math.abs(time - expectedTime) > 1 && expectedTime < startTime + 0.5) {
            // Probablement un temps obsolète après un seek, attendre la prochaine frame
            animationFrameId = requestAnimationFrame(updateTime)
            return
          }
          currentTimeRef.current = time

          // Mise à jour fluide du playhead en DOM direct (y compris dans les gaps entre accords)
          const playheadEl = playheadRef.current
          if (playheadEl) {
            const resolved = editorResolvedTimelineRef.current
            let leftPct
            if (resolved.contentWidthPx > 0 && resolved.segments.length > 0) {
              const segs = resolved.segments
              const contentPx = resolved.contentWidthPx
              let leftPx = 0
              let found = false
              for (let i = 0; i < segs.length; i++) {
                const s = segs[i]
                if (time >= s.startTime && time < s.endTime) {
                  const segDur = s.endTime - s.startTime
                  leftPx = segDur > 0 ? s.displayStartPx + (time - s.startTime) / segDur * s.displayWidthPx : s.displayStartPx
                  found = true
                  break
                }
              }
              if (!found) {
                if (time < segs[0].startTime) {
                  const span = segs[0].startTime - startTime
                  leftPx = span > 0 ? (time - startTime) / span * segs[0].displayStartPx : 0
                } else if (time >= segs[segs.length - 1].endTime) {
                  const last = segs[segs.length - 1]
                  const span = endTime - last.endTime
                  leftPx = span > 0 ? last.displayEndPx + (time - last.endTime) / span * (contentPx - last.displayEndPx) : last.displayEndPx
                } else {
                  for (let i = 0; i < segs.length - 1; i++) {
                    if (time >= segs[i].endTime && time < segs[i + 1].startTime) {
                      const a = segs[i]
                      const b = segs[i + 1]
                      const span = b.startTime - a.endTime
                      leftPx = span > 0 ? a.displayEndPx + (time - a.endTime) / span * (b.displayStartPx - a.displayEndPx) : a.displayEndPx
                      break
                    }
                  }
                }
              }
              leftPct = (leftPx / contentPx) * 100
            } else {
              leftPct = timelineRange > 0 ? ((time - startTime) / timelineRange) * 100 : 0
            }
            playheadEl.style.left = `${leftPct}%`
          }

          // setCurrentTime au changement de segment + toutes les 100 ms dans le segment pour que la jauge de progression se remplisse (comme en mode élève)
          const resolved = editorResolvedTimelineRef.current
          if (resolved.contentWidthPx > 0 && resolved.segments.length > 0) {
            let currentSegmentIndex = null
            for (let i = 0; i < resolved.segments.length; i++) {
              const s = resolved.segments[i]
              if (time >= s.startTime && time < s.endTime) {
                currentSegmentIndex = i
                break
              }
            }
            const segmentChanged = currentSegmentIndex !== lastSegmentIndexRef.current
            if (segmentChanged) {
              lastSegmentIndexRef.current = currentSegmentIndex
              lastSetStateTimeRef.current = Date.now()
              setCurrentTime(time)
            } else {
              const now = Date.now()
              if (now - lastSetStateTimeRef.current >= 50) {
                lastSetStateTimeRef.current = now
                setCurrentTime(time)
              }
            }
          } else {
            const now = Date.now()
            if (now - lastSetStateTimeRef.current >= 50) {
              lastSetStateTimeRef.current = now
              setCurrentTime(time)
            }
          }

        // Gérer le fade out avant la fin
        const timeUntilEnd = endTime - time
        if (timeUntilEnd <= fadeDuration && timeUntilEnd > 0) {
          // Fade out progressif
          const fadeOutProgress = 1 - (timeUntilEnd / fadeDuration)
          const volume = Math.max(0, 100 - (fadeOutProgress * 100))
          if (playerRef.current) {
            playerRef.current.setVolume(volume)
          }
        } else if (timeUntilEnd > fadeDuration && !fadeIntervalRef.current) {
          // S'assurer que le volume est à 100% pendant la lecture normale (après le fade in)
          // MAIS ne pas écraser un fade in en cours
          if (playerRef.current && !fadeIntervalRef.current) {
            playerRef.current.setVolume(100)
          }
        }

          if (time >= endTime) {
          // Nettoyer les fades en cours
          if (fadeIntervalRef.current) {
            clearInterval(fadeIntervalRef.current)
            fadeIntervalRef.current = null
          }
          // Restaurer le volume à 100% avant de mettre en pause
          playerRef.current.setVolume(100)
            playerRef.current.pauseVideo()
            setIsPlaying(false)
          setCurrentTime(time)
          isRunning = false
        } else {
          animationFrameId = requestAnimationFrame(updateTime)
          }
        } catch (error) {
          console.error('Erreur lors de la récupération du temps:', error)
        if (isRunning && isPlaying) {
          animationFrameId = requestAnimationFrame(updateTime)
        }
      }
    }

    if (isPlaying && playerRef.current) {
      // Nettoyer l'ancien interval si il existe
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      isRunning = true
      animationFrameId = requestAnimationFrame(updateTime)
    } else {
      isRunning = false
      // Synchroniser une dernière fois l'état React avec le temps actuel (surlignage segment)
      if (playerRef.current) {
        try {
          const t = playerRef.current.getCurrentTime()
          setCurrentTime(t)
          currentTimeRef.current = t
          const resolved = editorResolvedTimelineRef.current
          if (resolved.contentWidthPx > 0 && resolved.segments.length > 0) {
            let idx = null
            for (let i = 0; i < resolved.segments.length; i++) {
              if (t >= resolved.segments[i].startTime && t < resolved.segments[i].endTime) {
                idx = i
                break
              }
            }
            lastSegmentIndexRef.current = idx
          }
        } catch (_) {}
        playerRef.current.setVolume(100)
      }
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current)
        fadeIntervalRef.current = null
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    return () => {
      isRunning = false
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current)
        fadeIntervalRef.current = null
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isPlaying, endTime, startTime, timelineRange])

  // Gestion des raccourcis clavier globaux
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.code === 'Space' && !e.target.matches('input, textarea, [contenteditable]')) {
        e.preventDefault()
        handlePlayPause()
      }
    }

    window.addEventListener('keydown', handleGlobalKeyDown)
    return () => window.removeEventListener('keydown', handleGlobalKeyDown)
  }, [isPlaying])

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Parser MM:SS ou MM:SS.s pour la popover de saisie de temps
  const parseTimeInput = (timeString) => {
    if (!timeString || typeof timeString !== 'string') return null
    const trimmed = timeString.trim()
    const parts = trimmed.split(':')
    if (parts.length !== 2) return null
    const mins = parseInt(parts[0], 10)
    const secsParts = parts[1].replace(',', '.').split('.')
    const secs = parseInt(secsParts[0], 10)
    const frac = secsParts[1] ? parseInt(secsParts[1].slice(0, 1), 10) : 0
    if (isNaN(mins) || isNaN(secs) || mins < 0 || secs < 0) return null
    return mins * 60 + secs + frac / 10
  }

  const formatTimeDetailed = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = (seconds % 60).toFixed(1)
    return `${mins.toString().padStart(2, '0')}:${secs.padStart(4, '0')}`
  }

  // Format du temps relatif (pour le timer général)
  const formatRelativeTime = (seconds) => {
    const relativeTime = Math.max(0, seconds - startTime)
    return formatTimeDetailed(relativeTime)
  }

  const lastMarkerTimeRef = useRef(0)
  const createMarker = useCallback(() => {
    if (!playerRef.current) return
    
    const now = Date.now()
    if (now - lastMarkerTimeRef.current < 100) return
    lastMarkerTimeRef.current = now
    
    try {
      const absoluteTime = playerRef.current.getCurrentTime()
      
      if (absoluteTime >= startTime && absoluteTime <= endTime) {
        const isDuplicate = markers.some(m => Math.abs(m - absoluteTime) < 0.1)
        if (!isDuplicate) {
          const newMarkers = [...markers, absoluteTime].sort((a, b) => a - b)
          const insertIndex = newMarkers.findIndex(marker => marker === absoluteTime)

          setMarkers(newMarkers)
          setChordData(prev => {
            const newChordData = {}
            Object.entries(prev).forEach(([key, value]) => {
              const oldIndex = Number(key)
              if (Number.isNaN(oldIndex) || typeof value === 'undefined') return
              const newIndex = oldIndex >= insertIndex ? oldIndex + 1 : oldIndex
              newChordData[newIndex] = value
            })
            return newChordData
          })
          setChordEndTimes(prev => {
            const next = {}
            Object.entries(prev).forEach(([key, value]) => {
              const oldIndex = Number(key)
              if (oldIndex >= insertIndex) next[oldIndex + 1] = value
              else next[oldIndex] = value
            })
            return next
          })
          setSelectedMarkerId(prev =>
            prev !== null && prev >= insertIndex ? prev + 1 : prev
          )
          setFlash(true)
          setTimeout(() => setFlash(false), 200)
        }
      }
    } catch (error) {
      console.error('Erreur lors de la capture du timestamp:', error)
    }
  }, [startTime, endTime, markers])

  const handleEnterKey = useCallback((e) => {
    if (e.code === 'Enter' && playerRef.current) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return
      }
      e.preventDefault()
      createMarker()
    }
  }, [createMarker])

  useEffect(() => {
    window.addEventListener('keydown', handleEnterKey)
    return () => window.removeEventListener('keydown', handleEnterKey)
  }, [handleEnterKey])

  const handlePlayPause = () => {
    if (!playerRef.current) return

    if (isPlaying) {
      // Nettoyer les fades en cours
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current)
        fadeIntervalRef.current = null
      }
      // Restaurer le volume à 100% avant de mettre en pause
      playerRef.current.setVolume(100)
      playerRef.current.pauseVideo()
      setIsPlaying(false)
    } else {
      const current = currentTimeRef.current || currentTime
      const fadeDuration = 0.5 // Durée du fade en secondes

      // Si on est en dehors de l'extrait (avant ou après), revenir au début
      if (current < startTime || current >= endTime) {
        // Mettre à jour immédiatement les références de temps AVANT de lancer la lecture
        // pour éviter que updateTime détecte immédiatement la fin
        currentTimeRef.current = startTime
        setCurrentTime(startTime)
        playerRef.current.seekTo(startTime, true)
      }
      
      // Toujours appliquer un fade in au démarrage (comme en mode élève)
      playerRef.current.setVolume(0) // Commencer à volume 0 pour le fade-in
      // Mettre isPlaying à true APRÈS avoir mis à jour le temps pour éviter le conflit
      setIsPlaying(true)
      playerRef.current.playVideo()
      
      // Fade in progressif sur 0.5s (comme en mode élève)
      const fadeInSteps = 10
      const fadeInInterval = (fadeDuration * 1000) / fadeInSteps
      let fadeInStep = 0
      
      fadeIntervalRef.current = setInterval(() => {
        fadeInStep++
        const volume = Math.min(100, (fadeInStep / fadeInSteps) * 100)
        if (playerRef.current) {
          playerRef.current.setVolume(volume)
        }
        if (fadeInStep >= fadeInSteps) {
          if (fadeIntervalRef.current) {
            clearInterval(fadeIntervalRef.current)
            fadeIntervalRef.current = null
          }
        }
      }, fadeInInterval)
    }
  }

  const handlePlaySelection = () => {
    if (!playerRef.current) return
    
    const fadeDuration = 0.5
    
    playerRef.current.seekTo(startTime, true)
    playerRef.current.setVolume(0)
    playerRef.current.playVideo()
    setIsPlaying(true)
    
    const fadeInSteps = 10
    const fadeInInterval = (fadeDuration * 1000) / fadeInSteps
    let fadeInStep = 0
    
    const fadeInIntervalId = setInterval(() => {
      fadeInStep++
      const volume = Math.min(100, (fadeInStep / fadeInSteps) * 100)
      if (playerRef.current) {
        playerRef.current.setVolume(volume)
      }
      if (fadeInStep >= fadeInSteps) {
        clearInterval(fadeInIntervalId)
      }
    }, fadeInInterval)
    
    const checkEndInterval = setInterval(() => {
      if (!playerRef.current) {
        clearInterval(checkEndInterval)
        return
      }
      
      try {
        const currentTime = playerRef.current.getCurrentTime()
        const timeUntilEnd = endTime - currentTime
        
        if (timeUntilEnd <= fadeDuration && timeUntilEnd > 0) {
          const fadeOutProgress = 1 - (timeUntilEnd / fadeDuration)
          const volume = Math.max(0, 100 - (fadeOutProgress * 100))
          playerRef.current.setVolume(volume)
        }
        
        if (currentTime >= endTime) {
          playerRef.current.pauseVideo()
          playerRef.current.setVolume(100)
          setIsPlaying(false)
          clearInterval(checkEndInterval)
        }
      } catch (error) {
        console.error('Erreur lors du suivi de la fin:', error)
        clearInterval(checkEndInterval)
      }
    }, 50)
    
    const cleanup = () => {
      clearInterval(fadeInIntervalId)
      clearInterval(checkEndInterval)
      if (playerRef.current) {
        playerRef.current.setVolume(100)
      }
    }
    
    if (!window.editorFadeIntervals) {
      window.editorFadeIntervals = []
    }
    window.editorFadeIntervals.push({ fadeInIntervalId, checkEndInterval, cleanup })
  }

  const handleSeek = (time) => {
    if (!playerRef.current) return
    const wasPlaying = isPlaying
    playerRef.current.seekTo(time, true)
    setCurrentTime(time)
    currentTimeRef.current = time
    // Préserver l'état de lecture : si elle était en lecture, elle continue, sinon elle reste en pause
    if (wasPlaying) {
      // Si elle était en lecture, continuer la lecture après le seek
      playerRef.current.playVideo()
      setIsPlaying(true)
    } else {
      // Si elle était en pause, rester en pause
      playerRef.current.pauseVideo()
      setIsPlaying(false)
    }
  }

  const handleStartTimeChange = (newStartTime) => {
    setStartTime(Math.max(0, Math.min(newStartTime, endTime - 0.1)))
    if (playerRef.current) {
      playerRef.current.seekTo(newStartTime, true)
      // S'assurer que la vidéo reste en pause
      playerRef.current.pauseVideo()
      setIsPlaying(false)
    }
  }

  const handleEndTimeChange = (newEndTime) => {
    setEndTime(Math.max(startTime + 0.1, Math.min(newEndTime, videoDuration || 60)))
    // S'assurer que la vidéo reste en pause
    if (playerRef.current) {
      playerRef.current.pauseVideo()
      setIsPlaying(false)
    }
  }

  /** Fin du segment d'accord i (début du suivant ou endTime du dernier, ou chordEndTimes[i] si défini). */
  const getSegmentEndTime = useCallback((index) => {
    if (!markers.length || index < 0 || index >= markers.length) return endTime
    const nextStart = index < markers.length - 1 ? markers[index + 1] : endTime
    const customEnd = chordEndTimes[index]
    if (customEnd != null && customEnd > markers[index] && customEnd <= nextStart) {
      return customEnd
    }
    return nextStart
  }, [markers, endTime, chordEndTimes])

  /** Index du segment contenant le temps t, ou null si dans un trou / hors extrait. */
  const findCurrentSegmentIndex = useCallback((t) => {
    if (!markers.length || t < startTime || t > endTime) return null
    for (let i = 0; i < markers.length; i++) {
      const segEnd = getSegmentEndTime(i)
      if (t >= markers[i] && t < segEnd) return i
    }
    return null
  }, [markers, startTime, endTime, getSegmentEndTime])

  const handleCropChord = useCallback(() => {
    const t = currentTimeRef.current ?? currentTime
    const idx = findCurrentSegmentIndex(t)
    if (idx == null) return
    const segStart = markers[idx]
    const segEnd = getSegmentEndTime(idx)
    if (t <= segStart || t >= segEnd) return
    const clamped = Math.max(segStart + 0.01, Math.min(t, segEnd - 0.01))
    setChordEndTimes(prev => ({ ...prev, [idx]: clamped }))
  }, [markers, currentTime, findCurrentSegmentIndex, getSegmentEndTime])

  // Handlers pour le drag sur mobile et desktop (clic = popover, drag = overlay timeline)
  const isCoarsePointer = useCallback(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(pointer: coarse)').matches
  }, [])

  const cancelLongPress = useCallback(() => {
    const pending = longPressRef.current
    if (pending?.timerId) clearTimeout(pending.timerId)
    longPressRef.current = null
    setLongPressPendingIndex(null)
    setLongPressArmedIndex(null)
  }, [])

  const openTimerPopoverForTap = useCallback((wasType, wasMarkerIndex) => {
    timerJustOpenedRef.current = true
    setTimeout(() => { timerJustOpenedRef.current = false }, 500)
    if (wasType === 'in') {
      setEditingTimer({ type: 'in' })
      setTimerEditInputValue(formatTimeDetailed(startTime))
    } else if (wasType === 'out') {
      setEditingTimer({ type: 'out' })
      setTimerEditInputValue(formatTimeDetailed(endTime))
    } else if (wasType.startsWith('marker-') && wasMarkerIndex !== null) {
      setEditingTimer({ type: 'marker', index: wasMarkerIndex })
      setTimerEditInputValue(formatTimeDetailed(markers[wasMarkerIndex]))
    } else if (wasType.startsWith('crop-') && wasMarkerIndex !== null) {
      setEditingTimer({ type: 'crop', index: wasMarkerIndex })
      setTimerEditInputValue(formatTimeDetailed(getSegmentEndTime(wasMarkerIndex)))
    }
    setTimerEditError(null)
  }, [startTime, endTime, markers, getSegmentEndTime])

  const beginDragInteraction = useCallback((type, markerIndex, clientX) => {
    hasDraggedSinceTimerDownRef.current = false
    setDragStartX(clientX)
    dragLastXRef.current = clientX
    dragTotalPxRef.current = 0
    dragLastEventTimeRef.current = Date.now()
    setIsDraggingTime(type)
    if (type === 'in') {
      setDragStartTime(startTime)
      dragLastTimeRef.current = startTime
    } else if (type === 'out') {
      setDragStartTime(endTime)
      dragLastTimeRef.current = endTime
    } else if (type.startsWith('marker-') && markerIndex !== null) {
      const t = markers[markerIndex]
      setDragStartTime(t)
      setDraggingMarkerIndex(markerIndex)
      dragLastTimeRef.current = t
    } else if (type.startsWith('crop-') && markerIndex !== null) {
      const t = getSegmentEndTime(markerIndex)
      setDragStartTime(t)
      setDraggingMarkerIndex(markerIndex)
      dragLastTimeRef.current = t
    }
  }, [startTime, endTime, markers, getSegmentEndTime])

  const handleTimerMouseDown = (e, type, markerIndex = null) => {
    if (e.cancelable !== false) e.preventDefault()
    e.stopPropagation()
    timerAnchorRef.current = e.currentTarget
    const clientX = e.clientX || e.touches?.[0]?.clientX || 0
    beginDragInteraction(type, markerIndex, clientX)
  }

  const handleSegmentHandleTouchStart = useCallback((e, type, markerIndex) => {
    e.stopPropagation()
    if (!isCoarsePointer()) {
      handleTimerMouseDown(e, type, markerIndex)
      return
    }
    const touch = e.touches?.[0]
    if (!touch) return
    cancelLongPress()
    timerAnchorRef.current = e.currentTarget
    hasDraggedSinceTimerDownRef.current = false
    const pending = {
      type,
      markerIndex,
      startX: touch.clientX,
      startY: touch.clientY,
      target: e.currentTarget,
      activated: false,
      timerId: null,
    }
    pending.timerId = window.setTimeout(() => {
      pending.activated = true
      longPressRef.current = pending
      setLongPressPendingIndex(null)
      setLongPressArmedIndex(markerIndex)
      beginDragInteraction(type, markerIndex, touch.clientX)
    }, LONG_PRESS_MS)
    longPressRef.current = pending
    setLongPressPendingIndex(markerIndex)
  }, [isCoarsePointer, cancelLongPress, beginDragInteraction])

  const handleBoundaryTouchStart = useCallback((e, segIndex) => {
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    const mid = rect.left + rect.width / 2
    const x = e.touches?.[0]?.clientX ?? e.clientX ?? 0
    if (x < mid) handleSegmentHandleTouchStart(e, `crop-${segIndex}`, segIndex)
    else handleSegmentHandleTouchStart(e, `marker-${segIndex + 1}`, segIndex + 1)
  }, [handleSegmentHandleTouchStart])

  const handleTimerMouseMove = useCallback((e) => {
    if (!isDraggingTime) return

    const currentX = e.clientX || e.touches?.[0]?.clientX || 0
    const deltaX = currentX - dragLastXRef.current
    dragLastXRef.current = currentX
    dragTotalPxRef.current += Math.abs(deltaX)

    if (Math.abs(deltaX) > 5) {
      hasDraggedSinceTimerDownRef.current = true
    }

    const totalPx = dragTotalPxRef.current
    const deadZoneFactor = totalPx <= 0 ? 0.2 : Math.min(1, 0.2 + 0.8 * Math.min(1, totalPx / DRAG_DEADZONE_PX))

    let deltaTime
    if ((isDraggingTime.startsWith('marker-') || isDraggingTime.startsWith('crop-')) && draggingMarkerIndex !== null) {
      const resolved = editorResolvedTimelineRef.current
      if (resolved.contentWidthPx > 0 && resolved.segments[draggingMarkerIndex]) {
        const seg = resolved.segments[draggingMarkerIndex]
        const segDur = seg.endTime - seg.startTime
        const raw = seg.displayWidthPx > 0 ? deltaX * (segDur / seg.displayWidthPx) : deltaX * 0.1
        deltaTime = raw * DRAG_SENSITIVITY * deadZoneFactor
      } else {
        deltaTime = deltaX * 0.1 * DRAG_SENSITIVITY * deadZoneFactor
      }
    } else {
      deltaTime = deltaX * 0.1 * DRAG_SENSITIVITY * deadZoneFactor
    }

    if (isDraggingTime === 'in') {
      const newTime = Math.max(0, Math.min(endTime - 0.1, dragLastTimeRef.current + deltaTime))
      dragLastTimeRef.current = newTime
      handleStartTimeChange(newTime)
      setCurrentTime(newTime)
      currentTimeRef.current = newTime
    } else if (isDraggingTime === 'out') {
      const newTime = Math.max(startTime + 0.1, Math.min(videoDuration || 60, dragLastTimeRef.current + deltaTime))
      dragLastTimeRef.current = newTime
      handleEndTimeChange(newTime)
      setCurrentTime(newTime)
      currentTimeRef.current = newTime
    } else if (isDraggingTime.startsWith('marker-') && draggingMarkerIndex !== null) {
      const newTime = Math.max(startTime, Math.min(endTime, dragLastTimeRef.current + deltaTime))
      dragLastTimeRef.current = newTime
      const newMarkers = [...markers]
      newMarkers[draggingMarkerIndex] = newTime
      setMarkers(newMarkers.sort((a, b) => a - b))
      setCurrentTime(newTime)
      currentTimeRef.current = newTime
      if (playerRef.current) {
        playerRef.current.seekTo(newTime, true)
        playerRef.current.pauseVideo()
        setIsPlaying(false)
      }
    } else if (isDraggingTime.startsWith('crop-') && draggingMarkerIndex !== null) {
      const idx = draggingMarkerIndex
      const segStart = markers[idx]
      const nextStart = idx < markers.length - 1 ? markers[idx + 1] : endTime
      let newTime = Math.max(segStart + 0.01, Math.min(nextStart, dragLastTimeRef.current + deltaTime))
      dragLastTimeRef.current = newTime
      setCurrentTime(newTime)
      currentTimeRef.current = newTime
      if (newTime >= nextStart - 0.01) {
        setChordEndTimes(prev => {
          const next = { ...prev }
          delete next[idx]
          return next
        })
      } else {
        setChordEndTimes(prev => ({ ...prev, [idx]: newTime }))
      }
    }
  }, [isDraggingTime, endTime, startTime, videoDuration, draggingMarkerIndex, markers])

  const handleTimerMouseUp = useCallback(() => {
    const wasType = isDraggingTime
    const wasMarkerIndex = draggingMarkerIndex
    setIsDraggingTime(null)
    setDraggingMarkerIndex(null)
    cancelLongPress()
    if (!hasDraggedSinceTimerDownRef.current && wasType) {
      openTimerPopoverForTap(wasType, wasMarkerIndex)
    }
  }, [isDraggingTime, draggingMarkerIndex, cancelLongPress, openTimerPopoverForTap])

  useEffect(() => {
    if (isDraggingTime) {
      const onTouchMove = (e) => {
        if (isCoarsePointer() && (isDraggingTime.startsWith('marker-') || isDraggingTime.startsWith('crop-'))) {
          if (e.cancelable) e.preventDefault()
        }
        handleTimerMouseMove(e)
      }
      document.addEventListener('mousemove', handleTimerMouseMove)
      document.addEventListener('mouseup', handleTimerMouseUp)
      document.addEventListener('touchmove', onTouchMove, { passive: false })
      document.addEventListener('touchend', handleTimerMouseUp)
      
      return () => {
        document.removeEventListener('mousemove', handleTimerMouseMove)
        document.removeEventListener('mouseup', handleTimerMouseUp)
        document.removeEventListener('touchmove', onTouchMove)
        document.removeEventListener('touchend', handleTimerMouseUp)
      }
    }
  }, [isDraggingTime, handleTimerMouseMove, handleTimerMouseUp, isCoarsePointer])

  // Long press en attente : annuler si scroll, ouvrir popover si tap court
  useEffect(() => {
    const onTouchMove = (e) => {
      const pending = longPressRef.current
      if (!pending || pending.activated) return
      const touch = e.touches?.[0]
      if (!touch) return
      const dist = Math.hypot(touch.clientX - pending.startX, touch.clientY - pending.startY)
      if (dist > LONG_PRESS_CANCEL_PX) cancelLongPress()
    }
    const onTouchEnd = () => {
      const pending = longPressRef.current
      if (!pending || pending.activated) return
      const { type, markerIndex } = pending
      cancelLongPress()
      openTimerPopoverForTap(type, markerIndex)
    }
    const onTouchCancel = () => cancelLongPress()
    document.addEventListener('touchmove', onTouchMove, { passive: true })
    document.addEventListener('touchend', onTouchEnd)
    document.addEventListener('touchcancel', onTouchCancel)
    return () => {
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('touchend', onTouchEnd)
      document.removeEventListener('touchcancel', onTouchCancel)
    }
  }, [cancelLongPress, openTimerPopoverForTap])

  // Focus input à l'ouverture de la popover de saisie de temps
  useEffect(() => {
    if (editingTimer && timerEditInputRef.current) {
      timerEditInputRef.current.focus()
      timerEditInputRef.current.select()
    }
  }, [editingTimer])

  // Échap ferme la popover de saisie de temps
  useEffect(() => {
    if (!editingTimer) return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleTimerEditCancel()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [editingTimer])

  // Mesurer la largeur utile de la timeline (pour largeur min par segment)
  useEffect(() => {
    const el = timelineRef.current
    if (!el) return undefined
    const update = () => {
      const w = el.clientWidth || 0
      setEditorTimelineViewportWidth(w)
    }
    update()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    if (ro) ro.observe(el)
    window.addEventListener('resize', update)
    return () => {
      if (ro && el) ro.unobserve(el)
      window.removeEventListener('resize', update)
    }
  }, [markers.length])

  const handleTimerEditValidate = () => {
    const parsed = parseTimeInput(timerEditInputValue)
    if (parsed === null) {
      setTimerEditError('Format : MM:SS ou MM:SS.s')
      return
    }
    const maxDuration = videoDuration || 60
    if (editingTimer.type === 'in') {
      const clamped = Math.max(0, Math.min(parsed, endTime - 0.1))
      handleStartTimeChange(clamped)
      if (playerRef.current) {
        playerRef.current.seekTo(clamped, true)
        playerRef.current.pauseVideo()
        setIsPlaying(false)
      }
    } else if (editingTimer.type === 'out') {
      const clamped = Math.max(startTime + 0.1, Math.min(parsed, maxDuration))
      handleEndTimeChange(clamped)
    } else if (editingTimer.type === 'marker') {
      const clamped = Math.max(startTime, Math.min(parsed, endTime))
      const newMarkers = [...markers]
      newMarkers[editingTimer.index] = clamped
      setMarkers(newMarkers.sort((a, b) => a - b))
      if (playerRef.current) {
        playerRef.current.seekTo(clamped, true)
        playerRef.current.pauseVideo()
        setIsPlaying(false)
      }
    } else if (editingTimer.type === 'crop') {
      const idx = editingTimer.index
      const segStart = markers[idx]
      const nextStart = idx < markers.length - 1 ? markers[idx + 1] : endTime
      const clamped = Math.max(segStart + 0.01, Math.min(parsed, nextStart))
      setChordEndTimes(prev => ({ ...prev, [idx]: clamped }))
    }
    setEditingTimer(null)
    setTimerEditError(null)
  }

  const handleTimerEditCancel = () => {
    if (timerJustOpenedRef.current) return
    setEditingTimer(null)
    setTimerEditError(null)
  }

  const timerEditBackdropDismiss = useBackdropDismiss(handleTimerEditCancel)

  const handleTimerEditKeyDown = (e) => {
    if (e.key === 'Enter') handleTimerEditValidate()
    if (e.key === 'Escape') handleTimerEditCancel()
  }

  const handleMarkerClick = (index) => {
    setSelectedMarkerId(index)
    setIsModalOpen(true)
    if (playerRef.current) {
      playerRef.current.seekTo(markers[index], true)
      // S'assurer que la vidéo reste en pause
      playerRef.current.pauseVideo()
      setIsPlaying(false)
    }
  }

  const handleChordValidate = (chordDataFromModal) => {
    if (selectedMarkerId !== null) {
      setChordData(prev => ({
        ...prev,
        [selectedMarkerId]: chordDataFromModal
      }))
    }
    setIsModalOpen(false)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const getDuration = (index) => {
    if (index === 0) {
      return markers[index] - startTime
    }
    return markers[index] - markers[index - 1]
  }

  const handleDeleteMarker = (e, index) => {
    e.stopPropagation()
    const newMarkers = markers.filter((_, i) => i !== index)
    setMarkers(newMarkers)

    setChordData(prev => {
      const newChordData = {}
      Object.entries(prev).forEach(([key, value]) => {
        const oldIndex = Number(key)
        if (Number.isNaN(oldIndex) || oldIndex === index || typeof value === 'undefined') return
        const newIndex = oldIndex > index ? oldIndex - 1 : oldIndex
        newChordData[newIndex] = value
      })
      return newChordData
    })

    setChordEndTimes(prev => {
      const next = {}
      Object.entries(prev).forEach(([key, value]) => {
        const oldIndex = Number(key)
        if (oldIndex === index) return
        const newIndex = oldIndex > index ? oldIndex - 1 : oldIndex
        next[newIndex] = value
      })
      return next
    })

    setSelectedMarkerId(prev => {
      if (prev === null) return prev
      if (prev === index) return null
      if (prev > index) return prev - 1
      return prev
    })
  }

  const handleResetExercise = () => {
    const shouldReset = window.confirm(
      'Tout réinitialiser ?\n\nL\'exercice sera remis comme à l\'ouverture (aucun marqueur, pas de Set In/Out). Tous les accords seront supprimés.'
    )

    if (!shouldReset) return

    // Même état qu'un exercice vierge : startTime 0, endTime 60 → l'effet updateDuration mettra endTime à la durée vidéo
    setStartTime(0)
    setEndTime(60)
    setMarkers([])
    setChordData({})
    setChordEndTimes({})
    setSelectedMarkerId(null)
    setIsModalOpen(false)
    setEditingTimer(null)
    setTimerEditError(null)
    setDraggingMarkerIndex(null)
    setCurrentTime(0)
    currentTimeRef.current = 0
    setIsPlaying(false)

    // Ne pas appeler le player (seekTo/pauseVideo) pour éviter les erreurs YouTube si le DOM/iframe est instable
  }

  const handleVideoSelect = (videoData) => {
    setVideoId(videoData.id)
    setVideoTitle(videoData.title)
    const rawDuration = videoData.duration
    const durationSeconds = typeof rawDuration === 'number'
      ? rawDuration
      : (() => {
          const durationParts = String(rawDuration || '0').split(':').map(Number)
          return durationParts.length === 3
            ? durationParts[0] * 3600 + durationParts[1] * 60 + durationParts[2]
            : durationParts.length === 2
            ? durationParts[0] * 60 + durationParts[1]
            : durationParts[0] || 0
        })()
    setVideoDuration(durationSeconds)
    setIsEditingUrl(false)
    setShowVideoSearch(false)
  }

  const handleSaveExercise = async (metadata) => {
    if (!user) {
      try {
        await signInWithGoogle()
        setIsSaveModalOpen(true)
      } catch (error) {
        console.error('Erreur lors de la connexion:', error)
        if (error.code !== 'auth/popup-closed-by-user') {
          const { getAuthErrorMessage } = await import('../utils/errorHandler')
          setSaveMessage({ type: 'error', text: getAuthErrorMessage(error) })
          setTimeout(() => setSaveMessage(null), 5000)
        }
      }
      return
    }

    if (!extractedId || markers.length === 0) {
      setSaveMessage({ type: 'error', text: 'Veuillez charger une vidéo et créer au moins un marqueur' })
      setTimeout(() => setSaveMessage(null), 3000)
      return
    }

    setIsSaving(true)
    try {
      const chordArray = markers.map((_, i) => chordData[i] ?? null)
      const resolvedChords = inferBorrowedFromDegree(chordArray)
      const markersData = markers.map((markerTime, index) => {
        const nextMarkerTime = index < markers.length - 1 ? markers[index + 1] : endTime
        const end = chordEndTimes[index]
        const endTimeVal = (end != null && end > markerTime && end <= nextMarkerTime) ? end : undefined
        return {
          time: markerTime,
          chord: resolvedChords[index] || null,
          ...(endTimeVal != null && { endTime: endTimeVal })
        }
      })

      const exerciseData = {
        authorId: user.uid,
        authorName: user.displayName || user.email,
        status: metadata.privacy === 'public' ? 'published' : 'draft',
        video: {
          id: extractedId,
          title: videoTitle || 'Vidéo YouTube',
          duration: videoDuration
        },
        settings: {
          startTime: startTime,
          endTime: endTime,
          fadeIn: false,
          fadeOut: false
        },
        markers: markersData,
        metadata: {
          composer: metadata.composer,
          ...(metadata.composerGivenName != null && { composerGivenName: metadata.composerGivenName }),
          ...(metadata.composerFamilyName != null && { composerFamilyName: metadata.composerFamilyName }),
          workTitle: metadata.workTitle,
          movementTitle: metadata.movementTitle || null,
          exerciseTitle: metadata.exerciseTitle,
          subtitle: metadata.subtitle || null,
          difficulty: metadata.difficulty || null,
          ...(metadata.section != null && { section: metadata.section }),
          ...(metadata.musicCategory != null && { musicCategory: metadata.musicCategory }),
          ...(metadata.formation != null && { formation: Array.isArray(metadata.formation) ? metadata.formation : (metadata.formation ? [metadata.formation] : null) }),
          ...(metadata.genre != null && { genre: metadata.genre }),
          ...(metadata.year != null && { year: metadata.year }),
          ...(metadata.musicalEra != null && { musicalEra: metadata.musicalEra })
        },
        autoTags: metadata.autoTags || []
      }
      if (isAdminTeacher) {
        exerciseData.publicPreview = !!metadata.publicPreview
      }

      let savedId
      if (exerciseId && !metadata.saveAsCopy) {
        if (!canEditLoadedExercise) {
          setSaveMessage({ type: 'error', text: 'Vous n\'avez pas les droits pour modifier cet exercice. Utilisez "Sauvegarder copie".' })
          setTimeout(() => setSaveMessage(null), 5000)
          setIsSaving(false)
          return
        }
        await updateExercise(exerciseId, exerciseData)
        savedId = exerciseId
      } else {
        savedId = await createExercise(exerciseData)
        setExerciseId(savedId)
        if (!id) {
          navigate(`/editor/${savedId}`, { replace: true })
        }
      }
      
      // Sauvegarder l'état actuel comme état sauvegardé
      setLastSavedState({
        markers: [...markers],
        chordData: { ...chordData },
        chordEndTimes: { ...chordEndTimes },
        startTime,
        endTime,
        videoId: extractedId
      })
      
      setSaveMessage({ 
        type: 'success', 
        text: `Exercice ${exerciseId ? 'mis à jour' : 'sauvegardé'} !` 
      })
      setIsSaveModalOpen(false)
      
      setTimeout(() => setSaveMessage(null), 5000)
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
      setSaveMessage({ 
        type: 'error', 
        text: `Erreur lors de la sauvegarde: ${error.message}` 
      })
      setTimeout(() => setSaveMessage(null), 5000)
    } finally {
      setIsSaving(false)
    }
  }

  const processThumbnailBlob = async (blob) => {
    if (!exerciseId) return
    try {
      setIsThumbnailLoading(true)
      setThumbnailMessage({ type: 'info', text: 'Traitement…' })
      const dataUrl = await resizeImageToDataUrl(blob, 320, 180)
      await updateExercise(exerciseId, { thumbnailUrl: dataUrl })
      setThumbnailUrl(dataUrl)
      setThumbnailMessage({ type: 'success', text: 'Miniature mise à jour !' })
      setTimeout(() => setThumbnailMessage(null), 3000)
    } catch (err) {
      console.error('Thumbnail processing failed:', err)
      setThumbnailMessage({ type: 'error', text: err.message || 'Erreur' })
      setTimeout(() => setThumbnailMessage(null), 4000)
    } finally {
      setIsThumbnailLoading(false)
    }
  }

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    await processThumbnailBlob(file)
    e.target.value = ''
  }

  const handleThumbnailPaste = async (e) => {
    const blob = getImageFromPasteEvent(e)
    if (blob) {
      e.preventDefault()
      await processThumbnailBlob(blob)
    }
  }

  const handleThumbnailPasteButton = async () => {
    try {
      const blob = await getImageFromClipboard()
      if (blob) {
        await processThumbnailBlob(blob)
      } else {
        setThumbnailMessage({ type: 'error', text: 'Aucune image dans le presse-papiers' })
        setTimeout(() => setThumbnailMessage(null), 3000)
      }
    } catch (err) {
      setThumbnailMessage({ type: 'error', text: err.message })
      setTimeout(() => setThumbnailMessage(null), 4000)
    }
  }

  const handleThumbnailDelete = async () => {
    if (!exerciseId) return
    try {
      setIsThumbnailLoading(true)
      await updateExercise(exerciseId, { thumbnailUrl: null })
      setThumbnailUrl(null)
      setThumbnailMessage({ type: 'success', text: 'Miniature supprimée' })
      setTimeout(() => setThumbnailMessage(null), 3000)
    } catch (err) {
      console.error('Thumbnail delete failed:', err)
      setThumbnailMessage({ type: 'error', text: 'Erreur lors de la suppression' })
      setTimeout(() => setThumbnailMessage(null), 3000)
    } finally {
      setIsThumbnailLoading(false)
    }
  }

  const handleSaveClick = () => {
    if (!user) {
      signInWithGoogle()
        .then(() => setIsSaveModalOpen(true))
        .catch((error) => {
          console.error('Erreur lors de la connexion:', error)
          if (error.code !== 'auth/popup-closed-by-user') {
            import('../utils/errorHandler').then(({ getAuthErrorMessage }) => {
              alert(getAuthErrorMessage(error))
            })
          }
        })
    } else {
      if (!videoTitle || videoTitle === 'Vidéo YouTube') {
        try {
          if (playerRef.current) {
            const videoData = playerRef.current.getVideoData()
            if (videoData?.title) setVideoTitle(videoData.title)
          }
        } catch (_) {}
      }
      setIsSaveModalOpen(true)
    }
  }

  // Vérifier s'il y a des modifications non sauvegardées
  const hasUnsavedChanges = () => {
    // Si pas de marqueurs, pas de modifications
    if (markers.length === 0) {
      return false
    }
    
    // Si pas d'état sauvegardé, il y a des modifications
    if (!lastSavedState) {
      return true
    }
    
    // Comparer les marqueurs
    if (markers.length !== lastSavedState.markers.length) {
      return true
    }
    
    // Comparer les temps des marqueurs
    for (let i = 0; i < markers.length; i++) {
      if (Math.abs(markers[i] - lastSavedState.markers[i]) > 0.01) {
        return true
      }
    }
    
    // Comparer les accords
    const currentChordKeys = Object.keys(chordData).sort()
    const savedChordKeys = Object.keys(lastSavedState.chordData).sort()
    if (currentChordKeys.length !== savedChordKeys.length) {
      return true
    }
    
    for (let i = 0; i < currentChordKeys.length; i++) {
      if (currentChordKeys[i] !== savedChordKeys[i]) {
        return true
      }
      // Comparer les objets d'accord (simplifié)
      const currentChord = JSON.stringify(chordData[currentChordKeys[i]])
      const savedChord = JSON.stringify(lastSavedState.chordData[savedChordKeys[i]])
      if (currentChord !== savedChord) {
        return true
      }
    }
    
    // Comparer startTime et endTime
    if (Math.abs(startTime - lastSavedState.startTime) > 0.01 ||
        Math.abs(endTime - lastSavedState.endTime) > 0.01) {
      return true
    }
    
    // Comparer chordEndTimes
    const savedEndTimes = lastSavedState.chordEndTimes || {}
    const currentEndKeys = Object.keys(chordEndTimes).sort()
    const savedEndKeys = Object.keys(savedEndTimes).sort()
    if (currentEndKeys.length !== savedEndKeys.length) {
      return true
    }
    for (let i = 0; i < currentEndKeys.length; i++) {
      if (currentEndKeys[i] !== savedEndKeys[i]) return true
      if (Math.abs((chordEndTimes[currentEndKeys[i]] || 0) - (savedEndTimes[savedEndKeys[i]] || 0)) > 0.01) {
        return true
      }
    }

    // Comparer videoId
    const currentVideoId = extractVideoId(videoId) || videoId
    if (currentVideoId !== lastSavedState.videoId) {
      return true
    }
    
    return false
  }

  const handleBackClick = () => {
    if (hasUnsavedChanges()) {
      setPendingExitAction('dashboard')
      setIsConfirmExitModalOpen(true)
    } else {
      navigate(safeReturnTo)
    }
  }

  const handlePreviewClick = () => {
    if (!exerciseId) return
    if (hasUnsavedChanges()) {
      setPendingExitAction('preview')
      setIsConfirmExitModalOpen(true)
    } else {
      navigate(`/play/${exerciseId}`, { state: { returnTo: `/editor/${exerciseId}` } })
    }
  }

  const handleConfirmExit = () => {
    const action = pendingExitAction
    setIsConfirmExitModalOpen(false)
    setPendingExitAction(null)
    if (action === 'preview') {
      navigate(`/play/${exerciseId}`, { state: { returnTo: `/editor/${exerciseId}` } })
    } else {
      navigate(safeReturnTo)
    }
  }

  const EDITOR_MIN_SEGMENT_PX = 72

  // Segments pour la timeline (comme en mode élève) : un par accord, avec début/fin réels (crop éventuel)
  const editorChordSegments = useMemo(() => {
    if (!markers.length) return []
    return markers.map((marker, index) => {
      const segEnd = getSegmentEndTime(index)
      const segmentStart = marker - startTime
      const segmentEndRel = segEnd - startTime
      const startPos = timelineRange > 0 ? (segmentStart / timelineRange) * 100 : 0
      const endPos = timelineRange > 0 ? (segmentEndRel / timelineRange) * 100 : 100
      const width = Math.max(0, endPos - startPos)
      return { index, startTime: marker, endTime: segEnd, startPos, endPos, width }
    })
  }, [markers, startTime, timelineRange, getSegmentEndTime])

  // Résolution avec largeur min par segment + espace pour les trous (gaps) entre segments crop
  const editorResolvedTimeline = useMemo(() => {
    if (!editorChordSegments.length || editorTimelineViewportWidth <= 0) {
      return { segments: editorChordSegments, contentWidthPx: 0 }
    }
    const baseWidthPx = Math.max(editorTimelineViewportWidth, editorChordSegments.length * EDITOR_MIN_SEGMENT_PX, 1)
    const rawFirstStartPx = (editorChordSegments[0].startPos / 100) * baseWidthPx
    const rawLastEndPx = (editorChordSegments[editorChordSegments.length - 1].endPos / 100) * baseWidthPx
    const leadingGapPx = rawFirstStartPx
    const trailingGapPx = Math.max(0, baseWidthPx - rawLastEndPx)
    const rawSpanPx = rawLastEndPx - rawFirstStartPx
    let totalDisplayWidthPx = 0
    const resolvedWidths = editorChordSegments.map((seg) => {
      const rawWidthPx = (seg.width / 100) * baseWidthPx
      const displayWidthPx = Math.max(rawWidthPx, EDITOR_MIN_SEGMENT_PX)
      totalDisplayWidthPx += displayWidthPx
      return { rawWidthPx, displayWidthPx }
    })
    // Trous (gaps) entre segments : quand un accord est crop, réserver l'espace jusqu'au prochain
    let totalGapPx = 0
    const gapPxPerSegment = editorChordSegments.map((seg, i) => {
      if (i >= editorChordSegments.length - 1) return 0
      const gapDuration = Math.max(0, editorChordSegments[i + 1].startTime - seg.endTime)
      const gapPx = timelineRange > 0 ? (gapDuration / timelineRange) * baseWidthPx : 0
      totalGapPx += gapPx
      return gapPx
    })
    const extraNeededPx = Math.max(0, totalDisplayWidthPx - rawSpanPx)
    const availableGapPx = leadingGapPx + trailingGapPx
    const overflowBeyondGapsPx = Math.max(0, extraNeededPx - availableGapPx)
    const gapShrinkRatio = availableGapPx > 0 ? Math.min(1, extraNeededPx / availableGapPx) : 0
    const adjustedLeadingGapPx = leadingGapPx * (1 - gapShrinkRatio)
    let cursorPx = adjustedLeadingGapPx
    const contentWidthPx = Math.max(baseWidthPx + overflowBeyondGapsPx + totalGapPx, 1)
    const segments = editorChordSegments.map((seg, i) => {
      const { displayWidthPx } = resolvedWidths[i]
      const displayStartPx = cursorPx
      const displayEndPx = displayStartPx + displayWidthPx
      cursorPx = displayEndPx + (gapPxPerSegment[i] || 0)
      return {
        ...seg,
        displayStartPx,
        displayEndPx,
        displayWidthPx,
        displayStartPos: (displayStartPx / contentWidthPx) * 100,
        displayEndPos: (displayEndPx / contentWidthPx) * 100,
        displayWidthPercent: (displayWidthPx / contentWidthPx) * 100
      }
    })
    return { segments, contentWidthPx }
  }, [editorChordSegments, editorTimelineViewportWidth, EDITOR_MIN_SEGMENT_PX, timelineRange])

  const editorResolvedTimelineRef = useRef(editorResolvedTimeline)
  useEffect(() => {
    editorResolvedTimelineRef.current = editorResolvedTimeline
  }, [editorResolvedTimeline])

  // Formatage des accords pour l'affichage
  // Mapping des racines spéciales vers les fonctions
  const SPECIAL_ROOT_TO_FUNCTION = {
    'N': 'SD',   // Sixte napolitaine → Sous-Dominante
    'It': 'D',   // Sixte italienne → Dominante
    'Fr': 'D',   // Sixte française → Dominante
    'Gr': 'D'    // Sixte allemande → Dominante
  }

  // Fonction pour déterminer la fonction tonale d'un accord
  const getChordFunction = (chord, markerIndex = null) => {
    if (!chord) return null
    
    const { degree, specialRoot, selectedFunction } = chord
    
    // 1. Si une fonction est explicitement sélectionnée, l'utiliser
    if (selectedFunction) {
      return selectedFunction
    }
    
    // 2. Si c'est une racine spéciale, utiliser le mapping
    if (specialRoot) {
      return SPECIAL_ROOT_TO_FUNCTION[specialRoot] || null
    }
    
    // 3. Degré avec inférence contextuelle (ex. vi avant V → SD)
    if (degree) {
      const chordContext = markerIndex != null
        ? buildChordContext(
            markers.map((_, i) => chordData[i] || null),
            markerIndex
          )
        : {}
      return getDefaultFunctionForChord(chord, chordContext)
    }
    
    return null
  }

  const getDegreeLabel = getDegreeLabelForMode

  const formatChordLabel = (chord) => {
    if (!chord) return null
    
    // Extraire le degreeMode, avec 'generic' comme défaut seulement si vraiment absent
    // Si l'accord n'a pas de degreeMode, essayer de le récupérer depuis localStorage
    let degreeMode = chord.degreeMode
    if (!degreeMode) {
      const savedMode = localStorage.getItem('chordSelectorDegreeMode')
      degreeMode = savedMode || 'generic'
    }
    const { degree, accidental, quality, figure, isBorrowed, specialRoot, selectedFunction, sixFourVariant } = chord
    
    // Si seule une fonction est sélectionnée (sans degré)
    if (selectedFunction && !degree && !specialRoot) {
      return { 
        function: selectedFunction,
        isFunctionOnly: true
      }
    }
    
    if (specialRoot) {
      if (specialRoot === 'N') {
        // Adapter la napolitaine selon le mode
        const napolitaineLabel = getDegreeLabel('II', degreeMode)
        // Extraire le symbole ° si présent
        const hasDiminished = napolitaineLabel.includes('°')
        const degreeWithoutSymbol = napolitaineLabel.replace('°', '')
        return { 
          degree: degreeWithoutSymbol, 
          accidental: '♭', 
          figure: '6',
          quality: hasDiminished ? '°' : ''
        }
      } else {
        const special = { 'It': 'It', 'Fr': 'Fr', 'Gr': 'Gr' }[specialRoot]
        return { degree: special, figure: '+6' }
      }
    }
    
    if (!degree) return null
    
    // I64 dépendant du contexte : V64 (passage), cad64 (cadence), I64 (avancé)
    const isI64 = degree === 'I' && figure === '64'
    const displayDegreeRaw = isI64 && sixFourVariant === 'passing' ? 'V' : isI64 && sixFourVariant === 'cadential' ? 'cad' : degree
    const displayDegree = displayDegreeRaw === 'cad' ? 'Cad.' : getDegreeLabel(displayDegreeRaw, degreeMode)
    const hasDiminished = displayDegree.includes('°')
    const degreeWithoutSymbol = displayDegree.replace('°', '').trim()
    
    let finalQuality = quality
    if (hasDiminished && displayDegreeRaw !== 'cad') {
      if (!quality || quality === '°') {
        finalQuality = '°'
      }
    } else if (!quality) {
      finalQuality = ''
    }
    
    return {
      degree: degreeWithoutSymbol,
      accidental: displayDegreeRaw !== 'cad' ? (accidental === 'b' ? '♭' : accidental === '#' ? '♯' : accidental === 'natural' ? '♮' : '') : '',
      quality: finalQuality,
      figure: figure && figure !== '5' ? figure : '',
      isBorrowed
    }
  }

  // Trouver les cadences pour l'affichage
  const getCadences = useMemo(() => {
    const cadences = []
    markers.forEach((marker, index) => {
      const chord = chordData[index]
      if (chord?.cadence) {
        cadences.push({
          index,
          cadence: chord.cadence,
          startMarker: index > 0 ? index - 1 : null,
          endMarker: index
        })
      }
    })
    return cadences
  }, [markers, chordData])

  const timelineChords = useMemo(
    () => markers.map((_, i) => chordData[i] ?? null),
    [markers, chordData]
  )

  const timelineChordsWithBorrowed = useMemo(
    () => inferBorrowedFromDegree(timelineChords),
    [timelineChords]
  )

  const editorPedalOptions = useMemo(() => {
    const syntheticExercise = {
      markers: markers.map((t, i) => ({ chord: chordData[i] ?? null })),
      autoTags: loadedAutoTags
    }
    return {
      tonality: loadedTonality || 'C',
      mode: getExerciseDominantMode(syntheticExercise) || 'major'
    }
  }, [markers, chordData, loadedAutoTags, loadedTonality])

  // Groupes consécutifs emprunt / pédale (une parenthèse et un trait par groupe sur le ruban)
  const { borrowedGroups, pedalGroups } = useMemo(() => {
    const chords = timelineChordsWithBorrowed
    const runGroups = (predicate) => {
      const out = []
      let start = null
      for (let i = 0; i < chords.length; i++) {
        if (predicate(chords[i])) {
          if (start === null) start = i
        } else {
          if (start !== null) {
            out.push({ startIndex: start, endIndex: i - 1 })
            start = null
          }
        }
      }
      if (start !== null) out.push({ startIndex: start, endIndex: chords.length - 1 })
      return out
    }
    return {
      borrowedGroups: runGroups(c => c?.isBorrowed === true),
      pedalGroups: computePedalGroups(timelineChordsWithBorrowed, editorPedalOptions).groups
    }
  }, [timelineChordsWithBorrowed, editorPedalOptions])

  const opts = {
    height: '100%',
    width: '100%',
    playerVars: {
      autoplay: 0,
      controls: 0,
      modestbranding: 1,
      rel: 0
    }
  }

  if (loading) {
    return (
      <div className="editor-root h-screen w-screen bg-zinc-950 flex items-center justify-center text-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-400">Chargement de l'exercice...</p>
        </div>
      </div>
    )
  }

  const extractedId = extractVideoId(videoId) || videoId

  // Afficher VideoSearch si pas de vidéo ou en mode édition
  if (!extractedId || showVideoSearch || (isEditingUrl && !extractedId)) {
  return (
      <div className="editor-root h-screen w-screen bg-zinc-950 text-white overflow-hidden">
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-white/5 bg-zinc-900/50 backdrop-blur-md">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">Opus Lab</h1>
                <button 
                onClick={handleBackClick}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white transition-colors"
                >
                <ArrowLeft className="w-4 h-4" />
                Dashboard
                </button>
              </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            <VideoImport onVideoSelect={handleVideoSelect} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="editor-root h-screen w-screen overflow-hidden bg-zinc-950 text-white flex flex-col">
      {/* Zone HAUT (30%) - Lecteur Vidéo */}
      <div ref={videoZoneRef} className="editor-video-zone flex-shrink-0 h-[30vh] min-h-[200px] max-h-[300px] bg-zinc-900 border-b border-white/5 relative">
        <div className="absolute inset-0">
          {youtubePlayerError ? (
            <div className="w-full h-full flex items-center justify-center bg-zinc-900 text-white/80 text-sm p-4 text-center">
              Lecteur temporairement indisponible. Cliquez sur «&nbsp;Changer&nbsp;» pour réessayer ou choisir une autre vidéo.
            </div>
          ) : (
            <YouTubePlayerErrorBoundary>
              <YouTube
                videoId={extractedId}
                opts={opts}
                onReady={handleReady}
                onStateChange={handleStateChange}
                className="w-full h-full"
              />
            </YouTubePlayerErrorBoundary>
          )}
        </div>
            
        {/* Overlay avec contrôles et infos — en colonne sur mobile pour que Sauvegarder reste visible */}
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/60 to-transparent p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-shrink">
              <button
                onClick={handleBackClick}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 backdrop-blur-md transition-colors flex-shrink-0"
                title="Retour au Dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="px-3 py-2 sm:px-4 rounded-lg bg-black/40 backdrop-blur-md border border-white/10 min-w-0 flex-1 sm:flex-initial">
                <p className="text-sm font-medium text-white/90 truncate max-w-[140px] sm:max-w-md">{videoTitle || 'Vidéo YouTube'}</p>
              </div>
              <button
                onClick={() => setShowVideoSearch(true)}
                className="px-3 py-2 sm:px-4 rounded-lg bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/10 text-sm transition-colors flex-shrink-0"
              >
                Changer
              </button>
            </div>

            <div className="flex items-center justify-end gap-2 flex-shrink-0 flex-wrap max-w-full">
              {user ? (
                <>
                  <span className="hidden sm:inline px-3 py-1.5 rounded-lg bg-white/5 backdrop-blur-md text-sm text-white/70 truncate max-w-[120px] md:max-w-none">
                    {user.displayName || user.email}
                  </span>
                  <button
                    type="button"
                    onClick={handlePreviewClick}
                    disabled={!exerciseId}
                    title={exerciseId ? 'Voir en mode élève' : "Sauvegarder l'exercice pour le prévisualiser"}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors border border-white/10"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="text-sm font-medium">Preview</span>
                  </button>
                  {exerciseId && (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowThumbnailPanel((v) => !v)}
                        title="Miniature de l'exercice"
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors border border-white/10 relative"
                      >
                        {isThumbnailLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ImageIcon className="w-4 h-4" />
                        )}
                        <span className="text-sm hidden sm:inline">Miniature</span>
                        {thumbnailUrl && (
                          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-green-500 border border-zinc-900" />
                        )}
                      </button>
                      {showThumbnailPanel && (
                        <div
                          ref={thumbnailPanelRef}
                          tabIndex={-1}
                          onPaste={handleThumbnailPaste}
                          className="absolute right-0 top-full mt-2 w-72 bg-zinc-900 border border-white/10 rounded-xl shadow-2xl p-3 z-50 outline-none"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-white/70">Miniature</span>
                            <button type="button" onClick={() => setShowThumbnailPanel(false)} className="text-white/40 hover:text-white/70">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="rounded-lg overflow-hidden bg-black/40 border border-white/5 mb-2" style={{ aspectRatio: '16/9' }}>
                            {thumbnailUrl ? (
                              <img src={thumbnailUrl} alt="Miniature" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center text-white/30 text-xs gap-1 p-2 text-center">
                                <ImageIcon className="w-6 h-6 opacity-40" />
                                <span>Win+Shift+S pour capturer,</span>
                                <span>puis Ctrl+V ici</span>
                              </div>
                            )}
                          </div>
                          {thumbnailMessage && (
                            <p className={`text-xs mb-2 ${thumbnailMessage.type === 'success' ? 'text-green-400' : thumbnailMessage.type === 'error' ? 'text-rose-400' : 'text-blue-400'}`}>
                              {asDisplayText(thumbnailMessage, '')}
                            </p>
                          )}
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleThumbnailPasteButton}
                              disabled={isThumbnailLoading}
                              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-indigo-600/30 hover:bg-indigo-600/50 disabled:opacity-40 text-xs transition-colors border border-indigo-500/30"
                              title="Coller depuis le presse-papiers (Ctrl+V)"
                            >
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                              </svg>
                              Coller
                            </button>
                            <button
                              type="button"
                              onClick={() => thumbnailInputRef.current?.click()}
                              disabled={isThumbnailLoading}
                              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-40 text-xs transition-colors border border-white/10"
                            >
                              <Upload className="w-3 h-3" />
                              Fichier
                            </button>
                            {thumbnailUrl && (
                              <button
                                type="button"
                                onClick={handleThumbnailDelete}
                                disabled={isThumbnailLoading}
                                title="Supprimer la miniature"
                                className="flex items-center justify-center px-2 py-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 disabled:opacity-40 text-xs text-rose-400 transition-colors border border-rose-500/20"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          <input
                            ref={thumbnailInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleThumbnailUpload}
                            className="hidden"
                          />
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleResetExercise}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-500/15 hover:bg-rose-500/25 transition-colors border border-rose-400/30 text-rose-100"
                    title="Remettre l'exercice comme à l'ouverture (aucun marqueur, pas de Set In/Out), vider les accords"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span className="text-sm font-medium hidden sm:inline">Tout réinitialiser</span>
                    <span className="text-sm font-medium sm:hidden">Reset</span>
                  </button>
                  <button
                    onClick={handleSaveClick}
                    disabled={!extractedId || markers.length === 0}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-lg shadow-indigo-500/20"
                  >
                    <Save className="w-4 h-4" />
                    <span className="text-sm font-semibold">Sauvegarder</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={signInWithGoogle}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/10 transition-colors"
                >
                  <LogIn className="w-4 h-4" />
                  <span className="text-sm">Se connecter</span>
                </button>
              )}
            </div>
            </div>
          </div>
        </div>

      {/* Zone MILIEU - Timeline + timers + cadences ; flex-1 pour coller le control dock en bas */}
      <div className="flex-1 min-h-0 bg-zinc-950 flex flex-col overflow-y-auto">
        <div className="flex-1 min-h-0 flex flex-col p-4 gap-0 overflow-visible" style={{ paddingBottom: '8px' }}>
          {/* Timeline + timers + cadences : même logique desktop et mobile (flex-1) */}
          <div className="relative flex-1 min-h-0 flex flex-col" style={{ zIndex: 1 }}>
            <div 
              ref={timelineRef}
              className={`bg-zinc-900/50 backdrop-blur-md rounded-xl border border-white/5 p-4 relative cursor-pointer w-full flex flex-col flex-1 min-h-0 overflow-x-auto editor-timeline-scroll${isDraggingTime && (isDraggingTime.startsWith('marker-') || isDraggingTime.startsWith('crop-')) ? ' is-trim-dragging' : ''}`}
              onClick={(e) => {
                if (!playerRef.current) return
                const contentPx = editorResolvedTimeline.contentWidthPx
                const contentEl = timelineContentRef.current
                if (contentPx > 0 && contentEl && timelineRef.current) {
                  const rect = contentEl.getBoundingClientRect()
                  const scrollLeft = timelineRef.current.scrollLeft || 0
                  const contentX = e.clientX - rect.left + scrollLeft
                  const segs = editorResolvedTimeline.segments
                  for (let i = 0; i < segs.length; i++) {
                    const seg = segs[i]
                    if (contentX >= seg.displayStartPx && contentX < seg.displayEndPx) {
                      const segDur = seg.endTime - seg.startTime
                      const t = seg.startTime + (contentX - seg.displayStartPx) / seg.displayWidthPx * segDur
                      const clampedTime = Math.max(startTime, Math.min(endTime, t))
                      handleSeek(clampedTime)
                      return
                    }
                  }
                  handleSeek(endTime)
                  return
                }
                const rect = e.currentTarget.getBoundingClientRect()
                const padding = 16
                const clickX = e.clientX - rect.left - padding
                const availableWidth = rect.width - (padding * 2)
                const percentage = Math.max(0, Math.min(1, clickX / availableWidth))
                const newTime = startTime + (percentage * timelineRange)
                const clampedTime = Math.max(startTime, Math.min(endTime, newTime))
                handleSeek(clampedTime)
              }}
            >
              <div
                ref={timelineContentRef}
                className="flex flex-col flex-1 min-h-0"
                style={{
                  width: editorResolvedTimeline.contentWidthPx > 0 ? `${editorResolvedTimeline.contentWidthPx}px` : '100%',
                  minWidth: '100%'
                }}
              >
              {/* Cadences : au-dessus de la timeline accords (même référence % pour alignement) */}
              {getCadences.length > 0 && editorChordSegments.length > 0 && (
                <div className="relative w-full mb-1.5 h-6 pointer-events-none flex-shrink-0">
                  {getCadences.map((cadence, idx) => {
                    if (cadence.startMarker === null || cadence.endMarker === null) return null
                    const startIndex = cadence.startMarker
                    const endIndex = cadence.endMarker
                    const segStart = editorResolvedTimeline.contentWidthPx > 0 ? editorResolvedTimeline.segments[startIndex] : editorChordSegments[startIndex]
                    const segEnd = editorResolvedTimeline.contentWidthPx > 0 ? editorResolvedTimeline.segments[endIndex] : editorChordSegments[endIndex]
                    if (!segStart || !segEnd) return null
                    const leftPct = segStart.displayStartPos ?? segStart.startPos
                    const widthPct = (segEnd.displayEndPos ?? (segEnd.startPos + segEnd.width)) - leftPct
                    const cadenceLabels = {
                      perfect: 'Parfaite',
                      imperfect: 'Imparfaite',
                      plagal: 'Plagale',
                      rompue: 'Rompue',
                      évitée: 'Évitée',
                      'demi-cadence': 'Demi-cadence (½)',
                      half: 'Demi-cadence (½)'
                    }
                    return (
                      <div
                        key={idx}
                        className="absolute top-0"
                        style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                      >
                        <div className="absolute top-0 left-0 w-full h-4 border-t-2 border-l-2 border-r-2 border-amber-500/50 rounded-t-lg" />
                        <div className="absolute top-0.5 left-1/2 transform -translate-x-1/2 px-2 py-0.5 rounded bg-amber-500/20 backdrop-blur-sm border border-amber-500/30 text-xs font-semibold text-amber-300 whitespace-nowrap z-10">
                          {cadenceLabels[cadence.cadence] || cadence.cadence}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Timeline en segments (un par accord) - prend l'espace vertical disponible ; overflow visible pour que le curseur reste visible dans les zones sans accord */}
              <div className="relative min-w-full editor-timeline-segments flex-1 min-h-[5rem] overflow-visible">
                {/* Rail de fond toujours visible (plage IN → OUT) ; z-index 0 pour rester sous les segments */}
                <div className="editor-timeline-rail" aria-hidden="true" />
                {/* Segments */}
                {(editorResolvedTimeline.contentWidthPx > 0 ? editorResolvedTimeline.segments : editorChordSegments).map((seg) => {
                  const leftPct = seg.displayStartPos != null ? seg.displayStartPos : seg.startPos
                  const widthPct = seg.displayWidthPercent != null ? seg.displayWidthPercent : seg.width
                  const isSelected = selectedMarkerId === seg.index
                  const isCurrent = currentTime >= seg.startTime && currentTime < seg.endTime
                  const segDuration = seg.endTime - seg.startTime
                  const progressPct = segDuration > 0 && isCurrent
                    ? ((currentTime - seg.startTime) / segDuration) * 100
                    : isCurrent && currentTime >= seg.endTime ? 100 : 0
                  const nextStart = seg.index < markers.length - 1 ? markers[seg.index + 1] : endTime
                  const isAdjacentToNext = Math.abs(seg.endTime - nextStart) < 0.001
                  const prevEnd = seg.index > 0 ? getSegmentEndTime(seg.index - 1) : startTime
                  const isAdjacentToPrev = seg.index > 0 && Math.abs(markers[seg.index] - prevEnd) < 0.001
                  const chord = chordData[seg.index]
                  const chordLabel = formatChordLabel(chord)
                  const chordFunction = getChordFunction(chord, seg.index)
                  const borrowedGroup = borrowedGroups.find(g => seg.index >= g.startIndex && seg.index <= g.endIndex)
                  const borrowedGroupRole = borrowedGroup == null ? null : borrowedGroup.startIndex === borrowedGroup.endIndex ? null : seg.index === borrowedGroup.startIndex ? 'start' : seg.index === borrowedGroup.endIndex ? 'end' : 'middle'
                  const pedalGroup = pedalGroups.find(g => seg.index >= g.startIndex && seg.index <= g.endIndex)
                  const pedalGroupRole = pedalGroup && pedalGroup.endIndex > pedalGroup.startIndex ? (seg.index === pedalGroup.startIndex ? 'start' : seg.index === pedalGroup.endIndex ? 'end' : 'middle') : null
                  /* Masquer la pédale dans la case dès qu'il y a une bande continue : pas de trait I|II en doublon sous le 1er accord */
                  const hidePedalInLabel = pedalGroupRole != null
                  const effectivePedalDegree = chord?.pedalDegree ?? getImplicitPedalDegree(timelineChordsWithBorrowed, seg.index, editorPedalOptions)
                  const chordWithPedal = chord ? { ...chord, pedalDegree: effectivePedalDegree } : null
                  const segmentColorClass = chordFunction === 'T' ? 'editor-timeline-segment-t' : chordFunction === 'SD' ? 'editor-timeline-segment-sd' : chordFunction === 'D' ? 'editor-timeline-segment-d' : ''
                  const isTrimming = draggingMarkerIndex === seg.index || longPressArmedIndex === seg.index || longPressPendingIndex === seg.index
                  const trimHandleClass = (markerIndex) => {
                    if (longPressArmedIndex === markerIndex || draggingMarkerIndex === markerIndex) return ' is-trim-active'
                    if (longPressPendingIndex === markerIndex) return ' is-trim-armed'
                    return ''
                  }
                  return (
                    <div
                      key={seg.index}
                      className={`editor-timeline-segment ${segmentColorClass} ${isSelected ? 'editor-timeline-segment-selected' : ''} ${isCurrent ? 'editor-timeline-segment-current' : ''}${isTrimming ? ' editor-timeline-segment-trimming' : ''}`}
                      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                    >
                      {/* Handle gauche : drag début accord (masqué si frontière partagée avec segment précédent) */}
                      {!isAdjacentToPrev && (
                        <div
                          className={`editor-timeline-handle editor-timeline-handle-left${trimHandleClass(seg.index)}`}
                          title={`Début accord ${seg.index + 1}`}
                          onMouseDown={(e) => { e.stopPropagation(); handleTimerMouseDown(e, `marker-${seg.index}`, seg.index) }}
                          onTouchStart={(e) => { handleSegmentHandleTouchStart(e, `marker-${seg.index}`, seg.index) }}
                        />
                      )}
                      {/* Zone centrale : accord + barre de progression, clic = seek + sélection */}
                      <div
                        className="editor-timeline-segment-body"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleSeek(seg.startTime)
                          handleMarkerClick(seg.index)
                        }}
                      >
                        {isCurrent && (
                          <div
                            className="editor-timeline-segment-progress"
                            style={{ width: `${progressPct}%` }}
                          />
                        )}
                        <div className="editor-timeline-segment-chord">
                          {chordLabel ? (
                            chordLabel.isFunctionOnly ? (
                              <span className={`editor-timeline-segment-degree ${chordFunction === 'T' ? 'text-blue-400' : chordFunction === 'SD' ? 'text-violet-400' : 'text-pink-400'}`}>
                                {chordLabel.function}
                              </span>
                            ) : (
                              <ChordLabel
                                chord={chordWithPedal}
                                borrowedGroupRole={borrowedGroupRole}
                                hidePedalInLabel={hidePedalInLabel}
                                className="editor-timeline-segment-chord-label"
                              />
                            )
                          ) : (
                            <span className="editor-timeline-segment-empty">—</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleDeleteMarker(e, seg.index) }}
                          className="editor-timeline-segment-delete"
                          title="Supprimer l'accord"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      {/* Handle droit ou bande frontière (Option A) : fin accord ou début suivant */}
                      {isAdjacentToNext && seg.index < markers.length - 1 ? (
                        <div
                          className={`editor-timeline-boundary${trimHandleClass(seg.index)}${trimHandleClass(seg.index + 1)}`}
                          title={`Fin accord ${seg.index + 1} / Début accord ${seg.index + 2}`}
                          onMouseDown={(e) => {
                            e.stopPropagation()
                            const rect = e.currentTarget.getBoundingClientRect()
                            const mid = rect.left + rect.width / 2
                            const isLeft = (e.clientX || e.touches?.[0]?.clientX || 0) < mid
                            if (isLeft) handleTimerMouseDown(e, `crop-${seg.index}`, seg.index)
                            else handleTimerMouseDown(e, `marker-${seg.index + 1}`, seg.index + 1)
                          }}
                          onTouchStart={(e) => handleBoundaryTouchStart(e, seg.index)}
                        />
                      ) : (
                        <div
                          className={`editor-timeline-handle editor-timeline-handle-right${trimHandleClass(seg.index)}`}
                          title={`Fin accord ${seg.index + 1}`}
                          onMouseDown={(e) => { e.stopPropagation(); handleTimerMouseDown(e, `crop-${seg.index}`, seg.index) }}
                          onTouchStart={(e) => { handleSegmentHandleTouchStart(e, `crop-${seg.index}`, seg.index) }}
                        />
                      )}
                    </div>
                  )
                })}
                {/* Curseur de lecture : en lecture la position est pilotée par rAF (fluide) ; à l'arrêt par currentTime */}
                {currentTime >= startTime && currentTime <= endTime && (() => {
                  const contentPx = editorResolvedTimeline.contentWidthPx
                  const segs = editorResolvedTimeline.segments
                  let leftPct
                  if (contentPx > 0 && segs.length > 0) {
                    let leftPx = 0
                    let found = false
                    for (let i = 0; i < segs.length; i++) {
                      const s = segs[i]
                      if (currentTime >= s.startTime && currentTime < s.endTime) {
                        const segDur = s.endTime - s.startTime
                        leftPx = segDur > 0 ? s.displayStartPx + (currentTime - s.startTime) / segDur * s.displayWidthPx : s.displayStartPx
                        found = true
                        break
                      }
                    }
                    if (!found) {
                      if (currentTime < segs[0].startTime) {
                        const span = segs[0].startTime - startTime
                        leftPx = span > 0 ? (currentTime - startTime) / span * segs[0].displayStartPx : 0
                      } else if (currentTime >= segs[segs.length - 1].endTime) {
                        const last = segs[segs.length - 1]
                        const span = endTime - last.endTime
                        leftPx = span > 0 ? last.displayEndPx + (currentTime - last.endTime) / span * (contentPx - last.displayEndPx) : last.displayEndPx
                      } else {
                        for (let i = 0; i < segs.length - 1; i++) {
                          if (currentTime >= segs[i].endTime && currentTime < segs[i + 1].startTime) {
                            const a = segs[i]
                            const b = segs[i + 1]
                            const span = b.startTime - a.endTime
                            leftPx = span > 0 ? a.displayEndPx + (currentTime - a.endTime) / span * (b.displayStartPx - a.displayEndPx) : a.displayEndPx
                            break
                          }
                        }
                      }
                    }
                    leftPct = (leftPx / contentPx) * 100
                  } else {
                    leftPct = timelineRange > 0 ? ((currentTime - startTime) / timelineRange) * 100 : 0
                  }
                  return (
                    <div
                      ref={playheadRef}
                      className="absolute top-0 bottom-0 w-0.5 bg-amber-500 z-20 pointer-events-none"
                      style={isPlaying ? { willChange: 'left' } : { left: `${leftPct}%`, willChange: 'left' }}
                    >
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-3 h-3 rounded-full bg-amber-500 border-2 border-zinc-950" />
                    </div>
                  )
                })()}
              </div>

              {/* Bande pédale continue : un bloc par groupe (label + trait), alignée sur les segments */}
              {pedalGroups.length > 0 && (() => {
                const segs = editorResolvedTimeline.contentWidthPx > 0 ? editorResolvedTimeline.segments : editorChordSegments
                const getPedalRange = (g) => {
                  if (!segs.length) return null
                  const startSeg = segs[g.startIndex]
                  const endSeg = segs[g.endIndex]
                  if (!startSeg || !endSeg) return null
                  const leftPos = startSeg.displayStartPos ?? startSeg.startPos
                  const rightEnd = endSeg.displayEndPos ?? endSeg.endPos ?? (endSeg.startPos + (endSeg.width ?? 0))
                  return { leftPos, width: Math.max(0, rightEnd - leftPos) }
                }
                return (
                  <div className="editor-pedal-strip" aria-hidden>
                    {pedalGroups.map((g, idx) => {
                      const range = getPedalRange(g)
                      if (!range || range.width <= 0) return null
                      return (
                        <div key={idx} className="editor-pedal-strip-group" style={{ left: `${range.leftPos}%`, width: `${range.width}%` }} aria-label={`Pédale sur degré ${g.degree}`}>
                          <span className="editor-pedal-strip-label"><span className="editor-pedal-strip-label-word">Ped.</span> <span className="editor-pedal-strip-label-degree">{g.degree}</span></span>
                          <span className="editor-pedal-strip-line" />
                        </div>
                      )
                    })}
                  </div>
                )
              })()}

              {/* Timers Début / Fin alignés sous chaque segment (même largeur que la ligne segments pour alignement) */}
              <div className="editor-timeline-timers relative mt-1.5 flex-1 min-h-[4rem] min-w-0 w-full">
                {/* Légende en overlay pour ne pas réduire la largeur du conteneur timers */}
                <div className="absolute left-0 top-0 bottom-0 w-14 z-10 flex flex-col justify-stretch gap-0.5 pointer-events-none">
                  <span className="text-xs text-amber-400/90 font-semibold flex-1 min-h-0 flex items-center" title="Heure de début de l'accord">Début accord</span>
                  <span className="text-xs text-amber-600/90 font-semibold flex-1 min-h-0 flex items-center" title="Heure de fin de l'accord (crop)">Fin accord</span>
                </div>
                <div className="relative w-full h-full">
                {(editorResolvedTimeline.contentWidthPx > 0 ? editorResolvedTimeline.segments : editorChordSegments).map((seg) => {
                  const tLeft = seg.displayStartPos != null ? seg.displayStartPos : seg.startPos
                  const tWidth = seg.displayWidthPercent != null ? seg.displayWidthPercent : seg.width
                  return (
                  <div
                    key={seg.index}
                    className="absolute top-0 bottom-0 flex flex-col items-stretch justify-center gap-0.5 box-border overflow-hidden"
                    style={{ left: `${tLeft}%`, width: `${tWidth}%`, padding: '0 1px' }}
                  >
                    <div
                      onMouseDown={(e) => handleTimerMouseDown(e, `marker-${seg.index}`, seg.index)}
                      onTouchStart={(e) => handleTimerMouseDown(e, `marker-${seg.index}`, seg.index)}
                      className="flex-1 min-h-0 flex items-center justify-center rounded bg-zinc-800/90 border border-white/10 hover:border-indigo-500/50 text-xs md:text-sm font-mono text-white/60 whitespace-nowrap cursor-ew-resize select-none transition-colors"
                      title="Début — glisser pour ajuster"
                    >
                      {formatTime(markers[seg.index])}
                    </div>
                    <div
                      onMouseDown={(e) => handleTimerMouseDown(e, `crop-${seg.index}`, seg.index)}
                      onTouchStart={(e) => handleTimerMouseDown(e, `crop-${seg.index}`, seg.index)}
                      className="flex-1 min-h-0 flex items-center justify-center rounded bg-zinc-800/90 border border-white/10 hover:border-amber-500/50 text-xs md:text-sm font-mono text-white/50 whitespace-nowrap cursor-ew-resize select-none transition-colors"
                      title="Fin — glisser pour rogner"
                    >
                      {formatTime(getSegmentEndTime(seg.index))}
                    </div>
                  </div>
                  )
                })}
                </div>
              </div>

              {/* Bouton Ajouter accord */}
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={createMarker}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-dashed border-white/20 bg-white/5 hover:bg-white/10 hover:border-indigo-500/50 transition-all duration-200 text-white/60 hover:text-white/80"
                  title="Ajouter un accord"
                >
                  <Plus className="w-5 h-5" />
                  <span className="text-sm font-medium">Ajouter un accord</span>
                </button>
              </div>
              </div>
            </div>

          </div>

        </div>
      </div>

      {/* Zone BAS (30%) - Control Dock */}
      <div className="editor-control-dock flex-shrink-0 h-[30vh] min-h-[180px] max-h-[250px] bg-zinc-900/80 backdrop-blur-xl border-t border-white/5 p-4 md:p-6">
        {/* Layout Mobile : IN/OUT sur une seule ligne */}
        <div className="md:hidden flex flex-col h-full gap-3">
          {/* Ligne 1 : IN et OUT compacts sur une ligne */}
          <div className="flex items-center justify-between gap-2">
            {/* Groupe IN - Tout sur une ligne */}
            <div className="flex-1 flex items-center gap-2">
              <label className="text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap">IN</label>
              <button
                onClick={() => {
                  const current = currentTimeRef.current || currentTime
                  handleStartTimeChange(current)
                }}
                className="p-1.5 rounded-lg bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/30 transition-colors flex-shrink-0"
                title="Définir IN au temps actuel"
              >
                <Flag className="w-4 h-4 text-indigo-400" />
              </button>
              <div 
                ref={inTimerRef}
                onMouseDown={(e) => handleTimerMouseDown(e, 'in')}
                onTouchStart={(e) => handleTimerMouseDown(e, 'in')}
                className="flex-1 px-2 py-1.5 rounded-xl bg-zinc-800/50 backdrop-blur-sm border border-white/10 hover:border-indigo-500/50 transition-colors cursor-ew-resize active:bg-indigo-500/20 select-none"
                title="Glisser pour ajuster"
              >
                <div className="text-xs font-mono text-white font-bold text-center">
                  {formatTimeDetailed(startTime)}
                </div>
              </div>
            </div>

            {/* Groupe OUT - Tout sur une ligne */}
            <div className="flex-1 flex items-center gap-2">
              <label className="text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap">OUT</label>
              <button
                onClick={() => {
                  const current = currentTimeRef.current || currentTime
                  handleEndTimeChange(current)
                }}
                className="p-1.5 rounded-lg bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/30 transition-colors flex-shrink-0"
                title="Définir OUT au temps actuel"
              >
                <Flag className="w-4 h-4 text-indigo-400" />
              </button>
              <div 
                ref={outTimerRef}
                onMouseDown={(e) => handleTimerMouseDown(e, 'out')}
                onTouchStart={(e) => handleTimerMouseDown(e, 'out')}
                className="flex-1 px-2 py-1.5 rounded-xl bg-zinc-800/50 backdrop-blur-sm border border-white/10 hover:border-indigo-500/50 transition-colors cursor-ew-resize active:bg-indigo-500/20 select-none"
                title="Glisser pour ajuster"
              >
                <div className="text-xs font-mono text-white font-bold text-center">
                  {formatTimeDetailed(endTime)}
                </div>
              </div>
            </div>
          </div>

          {/* Ligne 2 : Transport + TAP */}
          <div className="flex items-center justify-center gap-3">
            {/* Bouton Revenir au début */}
            <button
              onClick={() => {
                handleSeek(startTime)
                setSelectedMarkerId(null)
              }}
              className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-zinc-800 border border-white/10 hover:border-white/20 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg active:scale-95"
              title="Revenir au début de l'extrait"
            >
              <RotateCcw className="w-5 h-5 md:w-6 md:h-6 text-white/70" />
            </button>

            {/* Bouton Prev */}
            <button
              onClick={() => {
                if (markers.length > 0 && selectedMarkerId !== null && selectedMarkerId > 0) {
                  const prevIndex = selectedMarkerId - 1
                  handleSeek(markers[prevIndex])
                  setSelectedMarkerId(prevIndex)
                } else {
                  // Si pas d'accord précédent, revenir au début de l'extrait (comme un marqueur fictif à 00:00)
                  handleSeek(startTime)
                  setSelectedMarkerId(null)
                }
              }}
              className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-zinc-800 border border-white/10 hover:border-white/20 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg active:scale-95"
              title="Marqueur précédent"
            >
              <SkipBack className="w-5 h-5 md:w-6 md:h-6 text-white/70" />
            </button>

            {/* Timer général + Bouton PLAY Héros */}
            <div className="flex flex-col items-center gap-2">
              {/* Timer général */}
              <div className="px-4 py-2 rounded-xl bg-zinc-800/50 backdrop-blur-sm border border-white/10">
                <div className="text-lg md:text-xl font-mono text-white font-bold text-center">
                  {formatRelativeTime(currentTime)}
                      </div>
              </div>
              {/* Bouton PLAY Héros */}
              <button
                onClick={handlePlayPause}
                className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 border-4 border-white/10 shadow-2xl shadow-indigo-500/40 hover:shadow-indigo-500/60 transition-all duration-200 flex items-center justify-center active:scale-95"
                title="Play/Pause (Espace)"
              >
                {isPlaying ? (
                  <Pause className="w-8 h-8 md:w-10 md:h-10 text-white" />
                ) : (
                  <Play className="w-8 h-8 md:w-10 md:h-10 text-white ml-1" />
                )}
              </button>
            </div>

            {/* Bouton Next */}
            <button
              onClick={() => {
                if (markers.length > 0 && selectedMarkerId !== null && selectedMarkerId < markers.length - 1) {
                  const nextIndex = selectedMarkerId + 1
                  handleSeek(markers[nextIndex])
                  setSelectedMarkerId(nextIndex)
                } else if (markers.length > 0) {
                  handleSeek(markers[0])
                  setSelectedMarkerId(0)
                } else {
                  handleSeek(endTime)
                }
              }}
              className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-zinc-800 border border-white/10 hover:border-white/20 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg active:scale-95"
              title="Marqueur suivant"
            >
              <SkipForward className="w-5 h-5 md:w-6 md:h-6 text-white/70" />
            </button>

            {/* Bouton TAP */}
            <div className="relative">
              <button
                onClick={createMarker}
                className="w-12 h-12 md:w-16 md:h-16 rounded-xl bg-zinc-800 border-2 border-white/10 hover:border-indigo-500/50 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-indigo-500/20 active:scale-95"
                title="TAP - Créer un marqueur (Entrée)"
              >
                <Plus className="w-6 h-6 md:w-7 md:h-7 text-indigo-400 font-bold stroke-[3]" />
              </button>
              <span className="hidden md:block text-xs text-white/50 font-mono absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                Entrée
                        </span>
            </div>

            {/* Bouton Crop */}
            <div className="relative">
              <button
                onClick={handleCropChord}
                disabled={markers.length === 0 || findCurrentSegmentIndex(currentTimeRef.current ?? currentTime) == null}
                className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-zinc-800 border border-white/10 hover:border-amber-500/50 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                title="Couper l'accord au temps actuel"
              >
                <Crop className="w-5 h-5 md:w-6 md:h-6 text-amber-400" />
              </button>
              <span className="hidden md:block text-xs text-white/50 font-mono absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                Crop
              </span>
            </div>
          </div>
        </div>

        {/* Layout Desktop : Une seule ligne symétrique */}
        <div className="hidden md:flex h-full items-center justify-between gap-4">
          {/* Groupe IN (Gauche) */}
          <div className="flex-shrink-0 flex flex-col gap-1 min-w-[100px]">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">IN</label>
              <button
                onClick={() => {
                  const current = currentTimeRef.current || currentTime
                  handleStartTimeChange(current)
                }}
                className="p-2 rounded-lg bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/30 transition-colors"
                title="Définir IN au temps actuel"
              >
                <Flag className="w-5 h-5 text-indigo-400" />
              </button>
            </div>
            <div 
              onMouseDown={(e) => handleTimerMouseDown(e, 'in')}
              onTouchStart={(e) => handleTimerMouseDown(e, 'in')}
              className="px-4 py-3 rounded-xl bg-zinc-800/50 backdrop-blur-sm border border-white/10 hover:border-indigo-500/50 transition-colors cursor-ew-resize active:bg-indigo-500/20 select-none"
              title="Glisser pour ajuster"
            >
              <div className="text-lg font-mono text-white font-bold text-center">
                {formatTimeDetailed(startTime)}
              </div>
            </div>
          </div>

          {/* Cluster Centre - Transport Controls */}
          <div className="flex-1 flex items-center justify-center gap-4">
            {/* Bouton Revenir au début */}
            <button
              onClick={() => {
                handleSeek(startTime)
                setSelectedMarkerId(null)
              }}
              className="w-14 h-14 rounded-xl bg-zinc-800 border border-white/10 hover:border-white/20 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg active:scale-95"
              title="Revenir au début de l'extrait"
            >
              <RotateCcw className="w-6 h-6 text-white/70" />
            </button>

            {/* Bouton Prev */}
            <button
              onClick={() => {
                if (markers.length > 0 && selectedMarkerId !== null && selectedMarkerId > 0) {
                  const prevIndex = selectedMarkerId - 1
                  handleSeek(markers[prevIndex])
                  setSelectedMarkerId(prevIndex)
                } else {
                  // Si pas d'accord précédent, revenir au début de l'extrait (comme un marqueur fictif à 00:00)
                  handleSeek(startTime)
                  setSelectedMarkerId(null)
                }
              }}
              className="w-14 h-14 rounded-xl bg-zinc-800 border border-white/10 hover:border-white/20 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg active:scale-95"
              title="Marqueur précédent"
            >
              <SkipBack className="w-6 h-6 text-white/70" />
            </button>

            {/* Timer général + Bouton PLAY Héros */}
            <div className="flex flex-col items-center gap-2">
              {/* Timer général */}
              <div className="px-4 py-2 rounded-xl bg-zinc-800/50 backdrop-blur-sm border border-white/10">
                <div className="text-xl font-mono text-white font-bold text-center">
                  {formatRelativeTime(currentTime)}
                </div>
              </div>
              {/* Bouton PLAY Héros */}
              <button
                onClick={handlePlayPause}
                className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 border-4 border-white/10 shadow-2xl shadow-indigo-500/40 hover:shadow-indigo-500/60 transition-all duration-200 flex items-center justify-center active:scale-95"
                title="Play/Pause (Espace)"
              >
                {isPlaying ? (
                  <Pause className="w-10 h-10 text-white" />
                ) : (
                  <Play className="w-10 h-10 text-white ml-1" />
                )}
              </button>
            </div>

            {/* Bouton Next */}
                      <button
              onClick={() => {
                if (markers.length > 0 && selectedMarkerId !== null && selectedMarkerId < markers.length - 1) {
                  const nextIndex = selectedMarkerId + 1
                  handleSeek(markers[nextIndex])
                  setSelectedMarkerId(nextIndex)
                } else if (markers.length > 0) {
                  handleSeek(markers[0])
                  setSelectedMarkerId(0)
                } else {
                  handleSeek(endTime)
                }
              }}
              className="w-14 h-14 rounded-xl bg-zinc-800 border border-white/10 hover:border-white/20 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg active:scale-95"
              title="Marqueur suivant"
            >
              <SkipForward className="w-6 h-6 text-white/70" />
                      </button>
                    </div>

          {/* Bouton TAP (Intégré discrètement) */}
          <div className="flex-shrink-0 relative">
            <button
              onClick={createMarker}
              className="w-16 h-16 rounded-xl bg-zinc-800 border-2 border-white/10 hover:border-indigo-500/50 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-indigo-500/20 active:scale-95"
              title="TAP - Créer un marqueur (Entrée)"
            >
              <Plus className="w-7 h-7 text-indigo-400 font-bold stroke-[3]" />
            </button>
            <span className="text-xs text-white/50 font-mono absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
              Entrée
            </span>
          </div>

          {/* Bouton Crop */}
          <div className="flex-shrink-0 relative">
            <button
              onClick={handleCropChord}
              disabled={markers.length === 0 || findCurrentSegmentIndex(currentTimeRef.current ?? currentTime) == null}
              className="w-14 h-14 rounded-xl bg-zinc-800 border border-white/10 hover:border-amber-500/50 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
              title="Couper l'accord au temps actuel"
            >
              <Crop className="w-6 h-6 text-amber-400" />
            </button>
            <span className="text-xs text-white/50 font-mono absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
              Crop
            </span>
          </div>

          {/* Groupe OUT (Droite) */}
          <div className="flex-shrink-0 flex flex-col gap-1 min-w-[100px]">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">OUT</label>
                <button
                  onClick={() => {
                    const current = currentTimeRef.current || currentTime
                    handleEndTimeChange(current)
                  }}
                  className="p-2 rounded-lg bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/30 transition-colors"
                  title="Définir OUT au temps actuel"
                >
                  <Flag className="w-5 h-5 text-indigo-400" />
                </button>
                    </div>
            <div 
              onMouseDown={(e) => handleTimerMouseDown(e, 'out')}
              onTouchStart={(e) => handleTimerMouseDown(e, 'out')}
              className="px-4 py-3 rounded-xl bg-zinc-800/50 backdrop-blur-sm border border-white/10 hover:border-indigo-500/50 transition-colors cursor-ew-resize active:bg-indigo-500/20 select-none"
              title="Glisser pour ajuster"
            >
              <div className="text-lg font-mono text-white font-bold text-center">
                {formatTimeDetailed(endTime)}
                  </div>
            </div>
          </div>
            </div>
          </div>

      {/* Flash effect */}
      {flash && (
        <div className="fixed inset-0 bg-white/20 pointer-events-none animate-pulse z-50"></div>
        )}

        {/* Modal de sélection d'accord */}
        <ChordSelectorModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onValidate={handleChordValidate}
          initialChord={selectedMarkerId !== null && chordData[selectedMarkerId] ? {
            ...chordData[selectedMarkerId],
            implicitPedalDegree: getImplicitPedalDegree(timelineChordsWithBorrowed, selectedMarkerId, editorPedalOptions)
          } : null}
        embedded={true}
        />
        
        {/* Modale de sauvegarde */}
        <SaveExerciseModal
          isOpen={isSaveModalOpen}
          onClose={() => setIsSaveModalOpen(false)}
          onSave={handleSaveExercise}
          isSaving={isSaving}
          videoId={extractedId}
          videoTitle={videoTitle}
          getVideoTitle={() => {
            try {
              return playerRef.current?.getVideoData?.()?.title ?? null
            } catch {
              return null
            }
          }}
          markers={markers}
          chordData={chordData}
          isEditMode={!!exerciseId}
          initialAutoTags={exerciseId ? loadedAutoTags : undefined}
          initialSection={exerciseId ? loadedSection : undefined}
          initialMusicCategory={exerciseId ? loadedMusicCategory : undefined}
          initialFormation={exerciseId ? loadedFormation : undefined}
          initialGenre={exerciseId ? loadedGenre : undefined}
          initialYear={exerciseId ? loadedYear : undefined}
          initialMusicalEra={exerciseId ? loadedMusicalEra : undefined}
          initialComposer={exerciseId ? loadedComposer : undefined}
          initialComposerGivenName={exerciseId ? loadedComposerGivenName : undefined}
          initialComposerFamilyName={exerciseId ? loadedComposerFamilyName : undefined}
          initialWorkTitle={exerciseId ? loadedWorkTitle : undefined}
          initialMovementTitle={exerciseId ? loadedMovementTitle : undefined}
          initialExerciseTitle={exerciseId ? loadedExerciseTitle : undefined}
          initialSubtitle={exerciseId ? loadedSubtitle : undefined}
          initialDifficulty={exerciseId ? loadedDifficulty : undefined}
          isAdminTeacher={isAdminTeacher}
          initialPublicPreview={exerciseId ? loadedPublicPreview : false}
        />
        
        {/* Modale de confirmation de sortie (retour dashboard ou ouverture preview) */}
        <ConfirmExitModal
          isOpen={isConfirmExitModalOpen}
          onClose={() => {
            setIsConfirmExitModalOpen(false)
            setPendingExitAction(null)
          }}
          onConfirm={handleConfirmExit}
          title={pendingExitAction === 'preview' ? "⚠️ Ouvrir l'aperçu" : "⚠️ Quitter l'éditeur"}
          message={pendingExitAction === 'preview'
            ? "Vous allez ouvrir l'aperçu. Les modifications non sauvegardées seront perdues."
            : "Vous allez quitter l'exercice. Les modifications non sauvegardées seront perdues."}
          warning={pendingExitAction === 'preview'
            ? "Sauvegardez d'abord si vous voulez prévisualiser avec vos dernières modifications."
            : "Assurez-vous d'avoir sauvegardé votre travail avant de quitter."}
          confirmLabel={pendingExitAction === 'preview' ? "Ouvrir l'aperçu sans sauvegarder" : "Quitter sans sauvegarder"}
        />

        {/* Popover saisie temps (timer IN/OUT ou marqueur) */}
        {editingTimer && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            {...timerEditBackdropDismiss}
            onPointerDown={(e) => {
              if (e.target !== e.currentTarget) return
              if (timerJustOpenedRef.current) {
                e.preventDefault()
                e.stopPropagation()
              }
            }}
            onKeyDown={(e) => e.key === 'Escape' && handleTimerEditCancel()}
            role="dialog"
            aria-modal="true"
            aria-label="Saisir le temps"
          >
            <div
              className="bg-zinc-900 border border-white/10 rounded-xl shadow-2xl p-4 w-full max-w-xs"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                {editingTimer.type === 'in' ? 'Début (IN)' : editingTimer.type === 'out' ? 'Fin (OUT)' : editingTimer.type === 'crop' ? `Fin accord ${editingTimer.index + 1}` : `Marqueur ${editingTimer.index + 1}`}
              </p>
              <input
                ref={timerEditInputRef}
                type="text"
                value={timerEditInputValue}
                onChange={(e) => {
                  setTimerEditInputValue(e.target.value)
                  setTimerEditError(null)
                }}
                onKeyDown={handleTimerEditKeyDown}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-white/10 text-white font-mono text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                placeholder="0:00.0"
                aria-label="Temps (MM:SS ou MM:SS.s)"
                aria-invalid={!!timerEditError}
                aria-describedby={timerEditError ? 'timer-edit-error' : undefined}
              />
              {timerEditError && (
                <p id="timer-edit-error" className="mt-2 text-xs text-rose-400" role="alert">
                  {timerEditError}
                </p>
              )}
              <div className="flex flex-col gap-2 mt-4">
                {editingTimer.type === 'crop' && (
                  <button
                    type="button"
                    onClick={() => {
                      const idx = editingTimer.index
                      setChordEndTimes(prev => {
                        const next = { ...prev }
                        delete next[idx]
                        return next
                      })
                      setEditingTimer(null)
                      setTimerEditError(null)
                    }}
                    className="w-full px-3 py-2 rounded-lg bg-zinc-600 hover:bg-zinc-500 text-white text-sm font-medium transition-colors"
                  >
                    Jusqu'au prochain
                  </button>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleTimerEditCancel}
                    className="flex-1 px-3 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-white text-sm font-medium transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleTimerEditValidate}
                    className="flex-1 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors"
                  >
                    Valider
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Message de notification */}
        {saveMessage && (
        <div className={`fixed top-4 right-4 px-6 py-4 rounded-xl backdrop-blur-md border shadow-2xl z-50 ${
          saveMessage.type === 'success' 
            ? 'bg-green-500/20 border-green-500/50 text-green-300' 
            : 'bg-rose-500/20 border-rose-500/50 text-rose-300'
        }`}>
            {asDisplayText(saveMessage, '')}
          </div>
        )}

      {/* PWA Install Prompt (Android uniquement) */}
      <PWAInstallPrompt />
    </div>
  )
}

export default Editor
